/**
 * Search corpus for Jyotisha topics, English labels + common synonyms (Hindi + simple transliteration).
 * Used for instant client-side lookup only.
 * Keep in sync with TAB_GROUPS (and hidden tabs) in JyotishaPage.jsx - every routable topic id should have one entry here.
 *
 * REMINDER: after changing topics/terms here, rebuild AskGuru RAG:
 *   cd takshify.ai && npm run build:bhrigu-rag
 * See takshify-shared/docs/com/BHRIGU_RAG.md
 */
import { detectJyotishaKindFromQuery, getJyotishaTopicKind } from '../utils/jyotishaTopicKinds';
import { SYLLABUS_TAB_IDS } from './jyotishaLessonNumbers';
import { RASHI_DEEP_DIVE_SEARCH_INDEX } from './jyotishaRashiDeepDiveSearchIndex';
import { GRAHA_GUIDE_SEARCH_INDEX } from './jyotishaGrahaGuideSearchIndex';

export const JYOTISHA_TOPIC_SEARCH_INDEX = [
  {
    id: 'intro-astrology-branches',
    groupLabel: 'प्रारंभ (Prarambh)',
    tabLabel: 'Simple overview',
    terms:
      'overview simple astrology jyotish jyotisha introduction siddhanta samhita hora branches natal predictive muhurta prashna karma map not jail three limbs parashari bphs',
  },
  {
    id: 'intro-astrology-building-blocks',
    groupLabel: 'प्रारंभ (Prarambh)',
    tabLabel: 'Building blocks',
    terms:
      'building blocks graha rashi nakshatra bhava navagraha twelve signs twenty seven mansions houses lagna dasha yog essentials planets rashis chart north indian',
  },
  {
    id: 'my-readings-western-vedic',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: 'West vs Vedic',
    terms:
      'western vedic tropical sidereal ayanamsa lagna nakshatra dasha equinox comparison psychology jyotish jyotisha western astrology personality karma predictive my readings',
  },
  {
    id: 'intro-kundali-types',
    groupLabel: 'प्रारंभ (Prarambh)',
    tabLabel: 'Kundali types',
    terms:
      'kundali kundli kundali types chart types varga shodashavarga D1 D2 D3 D4 D7 D9 D10 D12 D16 D20 D24 D27 D30 D40 D45 D60 hora drekkana navamsa dasamsa divisional zoom roadmap tier',
  },
  {
    id: 'intro-varga-calculation',
    groupLabel: 'प्रारंभ (Prarambh)',
    tabLabel: 'Calculate D3 & D12',
    terms:
      'drekkana d3 dreshkana d12 dwadashamsa dwadasamsa varga calculation how to calculate divisional 10 degree 2 degree 30 siblings parents worked example step by step',
  },
  {
    id: 'prashna-kundali',
    groupLabel: 'प्रारंभ (Prarambh)',
    tabLabel: 'Prashna Kundali',
    terms:
      'prashna prashna kundali horary question chart prashna marga kerala karya bhava lagna querent classical modern online consultation tithi vara yoga karana chandra chart प्रश्न कुंडली प्रश्न मार्ग',
  },
  {
    id: 'intro-chart-fixed-rules',
    groupLabel: 'प्रारंभ (Prarambh)',
    tabLabel: 'Chart rules',
    terms:
      'chart rules lagna ascendant north indian wheel houses fixed varga division dignity retrograde combustion papa paap graha malefic dusthana 6 8 12 papa sthana chandra moon tithi paksha shukla krishna ashtami saptami',
  },
  {
    id: 'rashis-janma-intro',
    groupLabel: 'राशि (Rashis)',
    tabLabel: 'What is rashi · Janma rashi',
    terms:
      'rashi janma rashi moon sign birth rashi chandra rashi nakshatra name naming syllable pada lagna ascendant sun sign western ephemeris longitude जन्म राशि चंद्र राशि नक्षत्र नामकरण लग्न सूर्य राशि',
  },
  {
    id: 'rashis-basics',
    groupLabel: 'राशि (Rashis)',
    tabLabel: '12 signs · nature & lords',
    terms:
      'rashi rashi rashis basics प्रारंभ introduction prarambh signs zodiac mesha vrishabha mithuna karka simha kanya tula vrischika dhanu makar kumbh meena twelve degrees booth stage graha actor lighting',
  },
  {
    id: 'rashis-char-sthir-dwiswabhav',
    groupLabel: 'राशि (Rashis)',
    tabLabel: 'Char · Sthir · Dwiswabhav',
    terms:
      'char chara movable sthir sthira fixed dwiswabhav dual mutable signs rashis swabhav mesha aries karka cancer tula libra makara capricorn vrishabha taurus simha leo vrishchik scorpio kumbh aquarius mithun gemini kanya virgo dhanu sagittarius meen pisces pattern 1 4 7 10 2 5 8 11 3 6 9 12 चर स्थिर द्विस्वभाव',
  },
  {
    id: 'mool-trikon-rashi',
    groupLabel: 'राशि (Rashis)',
    tabLabel: 'Mool Trikon Rashi',
    terms:
      'mool trikon mool trikona mul trikon own sign swakshetra svakshetra degrees jupiter guru sagittarius dhanu makara capricorn vrishchik scorpio lagna house bhava strength debilitated memory leo taurus cancer mercury virgo venus libra saturn aquarius mars aries workplace home param uchcha param neecha ucch neech dignity ladder 5 cancer capricorn',
  },
  {
    id: 'intro-yoga',
    groupLabel: 'प्रारंभ (Prarambh)',
    tabLabel: 'योग · Yog',
    terms:
      'yoga yogas raj dhan daridra vipreet neech bhang raj neecha bhanga neecha bhang debilitation cancellation kemadruma combination kendra trikona papa paap graha dusthana 6 8 12 papa sthana malefic',
  },
  {
    id: 'intro-hora',
    groupLabel: 'प्रारंभ (Prarambh)',
    tabLabel: 'Hora',
    terms:
      'hora planetary hour day night shukra budha choghadiya timing day lord',
  },
  {
    id: 'intro-nakshatra-dasha-guide',
    groupLabel: 'नक्षत्र (Nakshatra)',
    tabLabel: 'Birth time (first dasha)',
    terms:
      'birth time first dasha birth first dasha moon longitude janma moon to first dasha starting dasha vimshottari nakshatra mahadasha moon degree rohini ashwini balance degree table 13 20 360 lunar mansion vimshottari calculation first period',
  },
  {
    id: 'nakshatras',
    groupLabel: 'नक्षत्र (Nakshatra)',
    tabLabel: '27 mansions · charan & dasha',
    terms:
      'nakshatra नक्षत्र lunar mansion 27 charan pada quarter ashwini bharani charan wheel moon birth star',
  },
  {
    id: 'nakshatra-abhijit',
    groupLabel: 'नक्षत्र (Nakshatra)',
    tabLabel: 'अभिजित · Abhijit',
    terms:
      'abhijit abhijeet nakshatra 28th nakshatra hidden nakshatra intercalary uttara ashadha uttarashada shravana sravana muhurta muhurt midday makar capricorn vega lunar month vimshottari 27 nakshatras chandra wives victory undefeated bhagavatam 11.16.27 gita 10.21 margashirsha margasirsa purushartha kama electional panchang',
  },
  {
    id: 'nakshatra-yogatara',
    groupLabel: 'नक्षत्र (Nakshatra)',
    tabLabel: 'Yogatara · Main stars',
    terms:
      'yogatara yoga tara principal star reference fixed star constellation 27 nakshatra hindi sheratan pleiades aldebaran spica arcturus antares nakshatra main star Bayer Flamsteed traditional astronomy',
  },
  {
    id: 'nakshatra-profiles',
    groupLabel: 'नक्षत्र (Nakshatra)',
    tabLabel: 'Nakshatra personality notes',
    terms:
      'nakshatra personality notes birth star 27 moon nakshatra atlas wheel tendency traditional jyotisha ashwini bharani krittika rohini mrigashira lunar mansion notes',
  },
  {
    id: 'prarambh-ashtakavarga',
    groupLabel: 'प्रारंभ (Prarambh)',
    tabLabel: 'Ashtakavarga intro',
    terms:
      'ashtakavarga ashtakvarga bindu sav bav sarva bhinna gochar transit strength parashari 337 points eight contributors lagna jupiter saturn house strength prarambh introduction',
  },
  {
    id: 'ashtakavarga-six-levels',
    groupLabel: 'प्रारंभ (Prarambh)',
    tabLabel: 'Ashtakavarga six levels',
    terms:
      'ashtakavarga advanced bav sav trikona shodhana ekadhipatya shodhya pinda rashi gunakar graha gunakar reduced ashtakavarga shodhita transit prediction BPHS parashara six levels ladder',
  },
  {
    id: 'intro-dasha',
    groupLabel: 'प्रारंभ (Prarambh)',
    tabLabel: 'Dasha basics',
    terms:
      'dasha basics दशा vimshottari mahadasha antardasha balance ketu venus periods timing nakshatra lord 120 what is dasha',
  },
  {
    id: 'my-readings-shruti-vedangas',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: 'Shruti & Vedangas',
    terms:
      'shruti vedas vedangas shiksha kalpa vyakarana nirukta chandas jyotisha six limbs veda samhita brahmana aranyaka upanishad foundation my readings',
  },
  {
    id: 'prarambh-karma-shastra',
    groupLabel: 'प्रारंभ (Prarambh)',
    tabLabel: 'Karma · Shastra',
    terms:
      'karma shastra scripture gita bhagavad action reaction dharma adharma punya paap prarabdha sanchita agami',
  },
  {
    id: 'prarambh-six-branches',
    groupLabel: 'प्रारंभ (Prarambh)',
    tabLabel: 'Six Jyotish branches',
    terms:
      'six branches jyotish ganita samhita hora prashna muhurta nadi siddhanta predictive electional mundane branches angas',
  },
  {
    id: 'prarambh-five-lenses',
    groupLabel: 'प्रारंभ (Prarambh)',
    tabLabel: 'Five life lenses',
    terms:
      'five lenses dharma artha kama moksha arogya health wealth desire liberation purpose life areas purushartha',
  },
  {
    id: 'prarambh-karma-kundali',
    groupLabel: 'प्रारंभ (Prarambh)',
    tabLabel: 'Karma · Chart',
    terms:
      'karma kundali chart dasha transits houses planets lagna ninth house twelfth moksha bhava karma in horoscope',
  },
  {
    id: 'prarambh-navagraha-forces',
    groupLabel: 'प्रारंभ (Prarambh)',
    tabLabel: 'Navagraha forces',
    terms:
      'navagraha nine planets sun moon mars mercury jupiter venus saturn rahu ketu living forces devta graha worship remedies',
  },
  {
    id: 'prarambh-graha-signatures',
    groupLabel: 'प्रारंभ (Prarambh)',
    tabLabel: 'Graha signatures',
    terms:
      'graha signatures bphs brihat parashara nature color metal gem deity direction strength debilitation exaltation moolatrikona table attributes',
  },
  {
    id: 'intro-nakshatra-swami-mahadasha-years',
    groupLabel: 'नक्षत्र (Nakshatra)',
    tabLabel: 'Nakshatra -> Dasha years',
    terms:
      'nakshatra swami mahadasha years vimshottari table 27 ashwini bharani krittika rohini mrigashira ardra punarvasu pushya ashlesha magha phalguni hasta chitra swati vishakha anuradha jyeshtha moola ashadha shravana dhanishta shatabhisha bhadrapada revati ketu venus sun moon mars rahu jupiter saturn mercury dasha order pattern',
  },
  {
    id: 'intro-nakshatra-udu-dasha-distinction',
    groupLabel: 'नक्षत्र (Nakshatra)',
    tabLabel: 'Udu Dasha · key distinction',
    terms:
      'udu dasha nakshatra mahadasha vs planet mahadasha vimshottari ashtottari yogini shodashottari dwadashottari panchottari nakshatra lord sequence ketu venus sun moon mars rahu jupiter saturn mercury 120 years ashwini ketu rohini moon pushya saturn not ashwini mahadasha transit nakshatra sub lord nadi kp antardasha pratyantardasha confusion clarity',
  },
  {
    id: 'houses-bhava',
    groupLabel: 'गृह (Houses)',
    tabLabel: '12 Houses · life areas',
    terms:
      'bhava भाव house houses twelve chart kundli life areas lagna north indian figure papa paap graha dusthana 6 8 12 papa sthana leena sthana लीनस्थान concealed hidden 3rd 6th 8th 12th kendra trikona upachaya maraka trik houses overview body direction',
  },
  {
    id: 'bhava-body-parts',
    groupLabel: 'गृह (Houses)',
    tabLabel: 'Body Parts',
    terms:
      'body parts bhava body health houses cheat sheet lagna head face mouth teeth lungs chest heart stomach intestines kidneys reproductive organs chronic disease thighs knees calves feet left eye right eye house where problem occurs graha nature of problem',
  },
  {
    id: 'bhava-karma',
    groupLabel: 'गृह (Houses)',
    tabLabel: 'Karma',
    terms:
      'karma bhava houses soul purpose lessons papa paap graha dusthana 6 8 12 papa sthana malefic',
  },
  {
    id: 'bhava-graha-in-houses',
    groupLabel: 'गृह (Houses)',
    tabLabel: 'Graha in 12 Bhavas',
    terms:
      'graha planet in house bhava placement sun moon mars mercury jupiter venus saturn rahu ketu 12 houses chart interactive surya chandra mangal budh guru shukra shani',
  },
  {
    id: 'bhava-lords-in-houses',
    groupLabel: 'गृह (Houses)',
    tabLabel: 'House lords in all houses · भावेश सभी भावों में',
    terms:
      'house lord bhavesh lagnesh placement 1st 2nd 3rd 4th 5th 6th 7th 8th 9th 10th 11th 12th lord in house eight eighth fifth first second third fourth sixth seventh ninth tenth eleventh twelfth 8th lord in 5th 5th house lord in 5th house lesson 2.8 house lord placement bhava lordship अष्टमेश पंचम भाव भावेश capricorn saturn makar lagna whole sign parashari 144 combinations chart interactive',
  },
  {
    id: 'bhava-bhavat-bhavam',
    groupLabel: 'गृह (Houses)',
    tabLabel: 'Bhavat Bhavam',
    terms:
      'bhavat bhava bhavat bhavam bhavad bhavam house from house derived house partner house 2nd 3rd 7th 1st marriage wealth debt 11th lagna counting technique भावात् भावम्',
  },
  {
    id: 'bhava-dosha',
    groupLabel: 'गृह (Houses)',
    tabLabel: 'Dosha',
    terms:
      'dosha kendra adhipati kendradhipati manglik chandra dosha shani dosha rahu ketu kaal sarp grahan pitru daridra yoga benefic in kendra neutral challenge imbalance lesson bhava dosha',
  },
  {
    id: 'bhava-types',
    groupLabel: 'गृह (Houses)',
    tabLabel: 'Types',
    terms:
      'types kendra trikona trikon dusthana upachaya trishadaya panaphara apoklima maraka vyaya ayu classification bhava groups succedent cadent angular papa paap graha malefic 6 8 12 papa sthana leena sthana लीनस्थान concealed hidden house grouping',
  },
  {
    id: 'bhava-trees',
    groupLabel: 'गृह (Houses)',
    tabLabel: 'Trees',
    terms: 'trees nakshatra charan pada trees bhava association',
  },
  {
    id: 'planets-about-grahas',
    groupLabel: 'Gruh (Planets)',
    tabLabel: 'Navagraha · karaka & guides',
    terms:
      'graha grahas ग्रह planets navagraha sun moon mars mercury jupiter venus saturn rahu ketu surya chandra mangal guru shukra shani mitra मित्र friends friendly shatru शत्रु enemy enemies neutral sama सम natural friendship naisargika parashara papa paap graha malefic dusthana 6 8 12 papa sthana',
  },
  {
    id: 'planets-info',
    groupLabel: 'Gruh (Planets)',
    tabLabel: 'Lords & Signs',
    terms:
      'planet info table rashi color deity grain nature character signs lordship swami',
  },
  {
    id: 'planet-ucch-neech',
    groupLabel: 'Gruh (Planets)',
    tabLabel: 'Ucch & Neech',
    terms:
      'exaltation debilitation uccha neecha ucch neech param uchcha param neecha parma uchcha parma neecha degree 10 aries 3 taurus 28 capricorn 15 virgo 5 cancer 27 pisces 20 libra neecha bhanga deepest exaltation',
  },
  {
    id: 'calc-ucch-neech',
    groupLabel: 'कैलकुलेटर (Calculators)',
    tabLabel: 'Ucch / Neech Check',
    terms:
      'calculator ucch neech exaltation debilitation lagna kundali d1 degree retrograde vakri R sun moon mars mercury jupiter venus saturn swarashi own sign paramochcha paramneecha',
  },
  {
    id: 'calc-graha-combustion',
    groupLabel: 'कैलकुलेटर (Calculators)',
    tabLabel: 'Graha Combustion (Asta)',
    terms:
      'calculator combustion asta dagdha graha sun degree d1 lagna chart moon mars mercury jupiter venus saturn rahu ketu excluded angular distance',
  },
  {
    id: 'calc-drishti',
    groupLabel: 'कैलकुलेटर (Calculators)',
    tabLabel: 'Drishti Calculator',
    terms:
      'calculator drishti aspect graha bhavesh house lord lagna chart parashari mars jupiter saturn 4th 7th 8th 5th 9th 3rd 10th whole sign placement',
  },
  {
    id: 'calc-yog-check',
    groupLabel: 'कैलकुलेटर (Calculators)',
    tabLabel: 'Yog Checker',
    terms:
      'calculator yog yoga raj dhan daridra vipreet neech bhang buddhi sanyas kemadruma vish gaj kesari panch mahapurush ruchaka bhadra hamsa malavya sasa lagna house placement whole sign kendra trikona teaching',
  },
  {
    id: 'calc-dasha-basics',
    groupLabel: 'कैलकुलेटर (Calculators)',
    tabLabel: 'Dasha Calculator',
    terms:
      'calculator vimshottari dasha mahadasha antardasha balance moon nakshatra lord ketu venus sun birth star 120 years 13 20 degree sidereal timeline teaching intro dasha',
  },
  {
    id: 'calc-house-lord-placement',
    groupLabel: 'कैलकुलेटर (Calculators)',
    tabLabel: 'House lords & placement',
    terms:
      'calculator bhavesh sthiti भावेश स्थिति house lord placement bhava lord lagna ascendant sign lord 1st 2nd 3rd 4th 5th 6th 7th 8th 9th 10th 11th 12th 8th lord in 1st 8th lord in 5th 5th house 7th lord in 11th capricorn aries whole sign parashari teaching beginner chart eight fifth lord in house',
  },
  {
    id: 'calc-manglik',
    groupLabel: 'कैलकुलेटर (Calculators)',
    tabLabel: 'Manglik / Kuja Dosha',
    terms:
      'manglik kuja dosha mars house 1 2 4 7 8 12 marriage matching cancellation bhanga both partners manglik chandra lagna venus chart jupiter aspect yogakaraka calculator मांगलिक कुज दोष भंग',
  },
  {
    id: 'calc-navtara',
    groupLabel: 'कैलकुलेटर (Calculators)',
    tabLabel: 'Navtara Chakra',
    terms: 'navtara calculator janma nakshatra nine tara tara bala vipat kshema sampat pratyari naidhana mitra param mitra 27 nakshatras',
  },
  {
    id: 'calc-tara-bala-daily',
    groupLabel: 'कैलकुलेटर (Calculators)',
    tabLabel: 'Tara Bala',
    terms: 'tara bala daily nakshatra janma target star auspicious navtara vipat kshema',
  },
  {
    id: 'calc-gandmool',
    groupLabel: 'कैलकुलेटर (Calculators)',
    tabLabel: 'Gandmool Check',
    terms: 'gandmool gand mool nakshatra charan moon birth sensitive shanti',
  },
  {
    id: 'calc-panchadha-maitri',
    groupLabel: 'कैलकुलेटर (Calculators)',
    tabLabel: 'Panchadha Maitri',
    terms: 'panchadha maitri compound friendship natural temporary graha relation adhi mitra',
  },
  {
    id: 'calc-grah-yuddha',
    groupLabel: 'कैलकुलेटर (Calculators)',
    tabLabel: 'Grah Yuddha',
    terms: 'grah yuddha planetary war same sign degree winner mars mercury jupiter venus saturn',
  },
  {
    id: 'calc-kaal-sarp-type',
    groupLabel: 'कैलकुलेटर (Calculators)',
    tabLabel: 'Kaal Sarp Type',
    terms: 'kaal sarp kala sarpa rahu ketu house 12 types anant vasuki hemmed grahas',
  },
  {
    id: 'calc-pitru-dosha',
    groupLabel: 'कैलकुलेटर (Calculators)',
    tabLabel: 'Pitru Dosha Flags',
    terms: 'pitru dosha sun rahu ketu 9th house ancestral pitru karma',
  },
  {
    id: 'calc-kemadruma-shakata',
    groupLabel: 'कैलकुलेटर (Calculators)',
    tabLabel: 'Kemadruma / Shakata',
    terms: 'kemadruma shakata moon jupiter 6 8 12 second twelfth from moon',
  },
  {
    id: 'calc-digbala',
    groupLabel: 'कैलकुलेटर (Calculators)',
    tabLabel: 'Digbala',
    terms: 'digbala directional strength house planet sun 10th moon 4th mars 10th',
  },
  {
    id: 'calc-navamsa-lagna',
    groupLabel: 'कैलकुलेटर (Calculators)',
    tabLabel: 'Navamsa Lagna',
    terms: 'navamsa d9 lagna divisional varga degree pada marriage soul chart',
  },
  {
    id: 'calc-ashtakavarga-bindu',
    groupLabel: 'कैलकुलेटर (Calculators)',
    tabLabel: 'Ashtakavarga Bindu',
    terms: 'ashtakavarga sav bindu points sign strength 337 total',
  },
  {
    id: 'calc-neecha-bhanga',
    groupLabel: 'कैलकुलेटर (Calculators)',
    tabLabel: 'Neecha Bhanga',
    terms: 'neecha bhanga debilitation cancellation planet exaltation lord lagna moon',
  },
  {
    id: 'calc-varshaphal-lord',
    groupLabel: 'कैलकुलेटर (Calculators)',
    tabLabel: 'Varsh Lord',
    terms: 'varshaphal varsh lord annual chart age year muntha teaching',
  },
  {
    id: 'calc-tithi-vara-dosha',
    groupLabel: 'कैलकुलेटर (Calculators)',
    tabLabel: 'Tithi / Vara Hints',
    terms: 'tithi vara dosha weekday lunar day hints chaturthi ashtami saturday',
  },
  {
    id: 'calc-gulika',
    groupLabel: 'कैलकुलेटर (Calculators)',
    tabLabel: 'Gulika (Mandi)',
    terms:
      'gulika mandi upagraha saturn son malefic phaladeepika charak astro sage house 12 remedies shiva',
  },
  {
    id: 'calc-gochar-saturn-jupiter',
    groupLabel: 'कैलकुलेटर (Calculators)',
    tabLabel: 'Saturn Transit',
    terms: 'gochar saturn jupiter transit sign date preset lahiri sade sati approximate',
  },
  {
    id: 'planet-sign-transit',
    groupLabel: 'Gruh (Planets)',
    tabLabel: 'गोचर · Transit',
    terms:
      'transit gochar sign how long stay duration motion rashi months years rahu ketu',
  },
  {
    id: 'planet-digbala',
    groupLabel: 'Gruh (Planets)',
    tabLabel: 'Digbala',
    terms:
      'digbala directional strength dik bala mercury jupiter mars saturn houses',
  },
  {
    id: 'planet-deeptadi',
    groupLabel: 'Gruh (Planets)',
    tabLabel: 'Deeptadi',
    terms:
      'deeptadi deepta baladi six avastha state dignity deepta swastha mudita',
  },
  {
    id: 'planet-natural-friendship',
    groupLabel: 'Gruh (Planets)',
    tabLabel: 'Graha Mitra-Shatru',
    terms:
      'graha mitra shatru natural friendship naisargik friend enemy neutral sama planet relationship table',
  },
  {
    id: 'planet-friendship-compound',
    groupLabel: 'Gruh (Planets)',
    tabLabel: 'Mitra-Shatru relation',
    terms:
      'naisargik tatkalin natural temporary friendship compound relation mitra shatru ati mitra ati shatru sam neutral graha relation friendship table',
  },
  {
    id: 'planet-margi-vakri',
    groupLabel: 'Gruh (Planets)',
    tabLabel: 'Margi & Vakri (Retrograde)',
    terms:
      'margi vakri retrograde retro direct motion forward backward apparent R chart label cheshta bala shadbala vakra anuvakra atichari mercury retrograde jupiter saturn mars venus rahu ketu mean node pratigami मार्गी वक्री वक्र प्रतिगामी retrograde motion',
  },
  {
    id: 'planet-shadbala',
    groupLabel: 'Gruh (Planets)',
    tabLabel: 'Shadbala · Six Strengths',
    terms:
      'shadbala shad bala six fold strength sthana bala dig bala digbala kala bala kalabala cheshta bala naisargika bala drik bala drigbala virupa rupa shastiamsa planet strength report card functional capacity BPHS षड्बल स्थानबल दिग्बल कालबल चेष्टाबल नैसर्गिक दृक्बल विरूप रूप',
  },
  {
    id: 'planet-grah-yuddha',
    groupLabel: 'Gruh (Planets)',
    tabLabel: 'Grah Yuddha',
    terms: 'graha yuddha planetary war combustion same degree fight winner',
  },
  {
    id: 'planet-avastha',
    groupLabel: 'Gruh (Planets)',
    tabLabel: 'Avastha',
    terms:
      'avastha state baladi deepta jagrita supta dreaming planetary condition',
  },
  {
    id: 'planet-drishti',
    groupLabel: 'Gruh (Planets)',
    tabLabel: 'Drishti',
    terms:
      'drishti aspect glance full seventh special mars saturn jupiter houses',
  },
  {
    id: 'planet-gems',
    groupLabel: 'Gruh (Planets)',
    tabLabel: 'Gems',
    terms:
      'gemstone ratna ruby pearl coral emerald yellow sapphire diamond hessonite cats eye ring',
  },
  {
    id: 'planet-beej-mantras',
    groupLabel: 'Gruh (Planets)',
    tabLabel: 'Beej + Naam Mantras',
    terms:
      'beej mantra bija mantra naam mantra nama mantra seed mantra navagraha surya chandra mangal budh guru shukra shani rahu ketu om hraam hreem hraum shraam shreem shraum kraam kreem kraum braam breem braum graam greem graum draam dreem draum praam preem praum bhraam bhreem bhraum sraam sreem sraum planetary mantra graha mantra remedies upasana',
  },
  {
    id: 'planet-deities',
    groupLabel: 'Gruh (Planets)',
    tabLabel: 'Deities',
    terms:
      'deity devata graha god shiva vishnu hanuman lakshmi planetary deity',
  },
  {
    id: 'planet-avatars',
    groupLabel: 'Gruh (Planets)',
    tabLabel: 'Avatars',
    terms:
      'avatar avatara dashavatara rama krishna buddha planetary incarnation',
  },
  {
    id: 'planet-tatva',
    groupLabel: 'Gruh (Planets)',
    tabLabel: 'Elements',
    terms:
      'tatva tattva element fire earth air water ether pancha mahabhuta graha',
  },
  {
    id: 'planet-grains',
    groupLabel: 'Gruh (Planets)',
    tabLabel: 'Grains',
    terms:
      'grain anna wheat rice mung sesame barley planet grain association',
  },
  {
    id: 'planets-body',
    groupLabel: 'Gruh (Planets)',
    tabLabel: 'Body associations',
    terms:
      'body parts organs health marma tissues graha ayurveda physical',
  },
  {
    id: 'planets-tissues',
    groupLabel: 'Gruh (Planets)',
    tabLabel: 'Tissues',
    terms:
      'tissue dhatu siddha sapta dhatus blood bone marrow ayurveda graha',
  },
  {
    id: 'planet-plants',
    groupLabel: 'Gruh (Planets)',
    tabLabel: 'Plants',
    terms:
      'plants herbs trees medicinal graha botanical association',
  },
  {
    id: 'panchang-basics',
    groupLabel: 'पंचांग (Panchang)',
    tabLabel: 'Panchang Basics',
    terms:
      'panchang panchanga what is panchang five limbs panch ang tithi vara nakshatra yoga karana solar lunar calendar hindu calendar difference tithi date',
  },
  {
    id: 'panchang-vara',
    groupLabel: 'पंचांग (Panchang)',
    tabLabel: 'Vara (Weekday)',
    terms:
      'vara weekday sunday monday tuesday wednesday thursday friday saturday sun moon mars mercury jupiter venus saturn good bad activities day lord graha',
  },
  {
    id: 'panchang-tithi',
    groupLabel: 'पंचांग (Panchang)',
    tabLabel: 'Tithi (Lunar Day)',
    terms:
      'tithi lunar day 30 tithis shukla krishna paksha rikta purna nanda bhadra jaya calculation pratipada amavasya purnima moon sun angular distance',
  },
  {
    id: 'panchang-nakshatra',
    groupLabel: 'पंचांग (Panchang)',
    tabLabel: 'Nakshatra',
    terms:
      'nakshatra 27 nakshatras star group devata gana deva manushya rakshasa gandmool ashwini ashlesha magha jyeshtha moola revati tara bala navtara naming syllable',
  },
  {
    id: 'panchang-yoga',
    groupLabel: 'पंचांग (Panchang)',
    tabLabel: 'Yoga (Sun-Moon)',
    terms:
      'yoga panchang sun moon longitude sum 27 yogas auspicious inauspicious vishkumbha siddhi vyatipata vaidhriti shubha shiva saubhagya',
  },
  {
    id: 'panchang-karana',
    groupLabel: 'पंचांग (Panchang)',
    tabLabel: 'Karana',
    terms:
      'karana half tithi chara sthira bava balava kaulava taitila gara vanija vishti bhadra shakuni chatushpada naga kimstughna 11 karanas 60',
  },
  {
    id: 'panchang-27-yogas',
    groupLabel: 'पंचांग (Panchang)',
    tabLabel: '27 Yogas',
    terms:
      'panchang panchanga yoga 27 yogas auspicious inauspicious day quality siddha',
  },
  {
    id: 'panchang-bhadra-vishti',
    groupLabel: 'पंचांग (Panchang)',
    tabLabel: 'Bhadra, Vishti Karana',
    terms:
      'bhadra vishti karana karan bhadra kaal kaal panchang paksha shukla krishna tithi half lunar day mukha puchha tail vrischika sarpini scorpion snake muhurta',
  },
  {
    id: 'panchang-muhurat',
    groupLabel: 'पंचांग (Panchang)',
    tabLabel: 'Muhurat Basics',
    terms:
      'muhurat muhurta timing auspicious time rahu kaal gulika kaal yamaganda panchaka election electional lagna favorable unfavorable',
  },
  {
    id: 'panchang-doshas',
    groupLabel: 'पंचांग (Panchang)',
    tabLabel: 'Doshas in Panchang',
    terms:
      'dosha bhadra dosha panchaka dosha dagdha rashi tithi gandanta eclipse grahan effect burnt combination junction water fire',
  },
  {
    id: 'panchang-festivals',
    groupLabel: 'पंचांग (Panchang)',
    tabLabel: 'Festivals & Panchang',
    terms:
      'festival calculation amavasya purnima ekadashi sankranti holi diwali navratri chaturmas devshayani tithi month paksha hindu calendar',
  },
  {
    id: 'panchang-moon-transit',
    groupLabel: 'पंचांग (Panchang)',
    tabLabel: 'Moon Transit',
    terms:
      'chandra bala tara bala gochar moon transit daily panchang reading navtara chakra birth nakshatra rashi favorable unfavorable',
  },
  {
    id: 'panchang-advanced',
    groupLabel: 'पंचांग (Panchang)',
    tabLabel: 'Advanced Panchang',
    terms:
      'hora choghadiya abhijit muhurat planetary war graha yuddha combustion asta uday vakri retrograde amrit shubh labh rog kaal udveg',
  },
  {
    id: 'panchang-texts',
    groupLabel: 'पंचांग (Panchang)',
    tabLabel: 'Traditional Texts',
    terms:
      'muhurta chintamani brihat samhita dharma sindhu kalaprakashika traditional text grantha surya siddhanta ayanamsha lahiri calculation panchang basics',
  },
  {
    id: 'charts-d1-vargas-overview',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'Overview',
    terms:
      'D chart meaning 30 degree sign divide varga shodashavarga D1 D2 D3 D4 D7 D9 D10 D12 D16 D20 D24 D27 D30 D40 D45 D60 navamsa 3 degree 20 minute dasamsa trimsamsa unequal divisional rashi lagna birth chart zoom lens',
  },
  {
    id: 'reading-charts-navamsa-91-labha',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'D-9 · 91st Navamsa · Labha',
    terms:
      'navamsa D9 ninth division 91 navamsha 91st labha लाभ नवांश eleventh house 11th lord gain wealth income sutra mantra parashara varga d1 house 11 planet in navamsa house 7 seventh divisional chart income finance quality quantity lagna lord friendship graha mitra dhana yog shukra shani saturn venus prosperity',
  },
  {
    id: 'charts-lagna-basic',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'Lagna Analysis',
    terms:
      'lagna basics lagna-wise lagna wise ascendant mesha vrishabha functional benefic malefic maraka trikona dusthana kendra house lord ownership shubh ashubh mixed lagnesh chart without planets papa paap graha sun moon mars mercury jupiter venus saturn rahu ketu 6 8 12 papa sthana',
  },
  {
    id: 'my-readings-arudha-lagna',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: 'आरूढ लग्न (जैमिनी पद्धति) · Arudha Lagna',
    terms:
      'my readings arudha lagna arudh pada AL jaimini special lagna public image reputation perception projection social status worldly image calculation count from ascendant lord exception 1st 7th 10th 4th jagannatha hora pada chart reading maya mask upapada आरूढ लग्न जैमिनी पद्धति',
  },
  {
    id: 'reading-charts-bhava-grades',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'Planet / Bhava Grade',
    terms:
      'planet bhava grade bhava grades house grades planetary grade analysis trikona kendra dusthana maraka a grade b grade c grade d grade 1 5 9 4 7 10 2 12 3 6 8 12 house classification significator numbers',
  },
  {
    id: 'reading-charts-power-of-trikonas',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'त्रिकोणों की शक्ति · Power of Trikonas',
    terms:
      'power of trikonas trikonon ki shakti त्रिकोणों की शक्ति trikona trikon introduction parichay परिचय prastavna dharma trikon artha trikon kama trikon moksha trikon 1 5 9 2 6 10 3 7 11 4 8 12 houses trine 120 degrees purushartha wealth career service debt rivalry gains partnership marriage friends sukha randhra vyaya lakshmi lakshmisthana purva punya dhri life purpose kendra dusthana upachaya trik trikona lords chart reading jupiter guru dharma karma adhipati raj yoga',
  },
  {
    id: 'reading-charts-gochar-basics',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'What is Gochar?',
    terms:
      'gochar gocchar transit what is gochar current movement planets live movement janma rashi moon counting lagna dasha trigger birth chart promise shani gochar guru gochar saturn transit jupiter transit rahu transit sade sati dhaiya kantaka shani vedha ashtakavarga slow fast graha',
  },
  {
    id: 'reading-charts-ashtakavarga',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'अष्टकवर्ग · Ashtakavarga',
    terms:
      'ashtakavarga ashtakvarga astakavarga bav sav bhinna sarva bindu points eight contributors sun moon mars mercury jupiter venus saturn lagna transits house strength gochar vedic',
  },
  {
    id: 'quiz-daily',
    groupLabel: 'परीक्षा दें (Test yourself)',
    tabLabel: 'दैनिक परीक्षा · Daily',
    terms: 'daily exam pariksha today quiz jyotisha test practice multiple choice gochar dasha bhava rashi drishti',
  },
  {
    id: 'quiz-practice',
    groupLabel: 'परीक्षा दें (Test yourself)',
    tabLabel: 'अभ्यास परीक्षा · Practice',
    terms: 'practice exam pariksha chapter lesson area quiz jyotisha scoped test syllabus topic',
  },
  {
    id: 'reading-charts-planet-12-houses',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'Planet in 12 Bhavas',
    terms:
      'planet in bhavas planet in houses cheat sheet planet in all 12 houses planet in 12 bhavas sun moon mars mercury jupiter venus saturn rahu ketu surya chandra mangal budh guru shukra shani first house second house third fourth fifth sixth seventh eighth ninth tenth eleventh twelfth quick meanings what where how sign house bhava dasha reading charts',
  },
  {
    id: 'reading-charts-foreign-travel-yoga',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'Foreign Travel Yog',
    terms:
      'foreign travel yoga abroad settlement 12th house rahu 9th house 3rd house 10th lord in 12th lagna lord in 12th moon in 12th moon rahu fourth lord in 12th ketu in 4th mars in 12th move away from birthplace foreign job foreign country dasha support',
  },
  {
    id: 'intro-gandmool',
    groupLabel: 'नक्षत्र (Nakshatra)',
    tabLabel: 'Gandmool Nakshatra',
    terms:
      'gandmool gand mool gandmula gand mula gandmool nakshatra gandmool dosha gand mool dosha gandanta gandant moon birth charan pada quarter shanti puja 27th day ashlesha magha moola mula revati jyeshtha ashwini family parents siblings sensitive junction rashi sandhi zodiac boundary',
  },
  {
    id: 'reading-charts-tithi-dosha',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'Tithi Dosha',
    terms:
      'tithi dosha तिथि दोष lunar day panchang muhurta krishna shukla paksha chaturthi navami chaturdashi amavasya moon sun angle electional',
  },
  {
    id: 'reading-charts-grahan-dosha',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'Grahan Dosha',
    terms:
      'grahan dosha ग्रहण दोष eclipse rahu ketu sun moon conjunction luminary nodes affliction',
  },
  {
    id: 'reading-charts-sun-major-doshas',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'Sun Major Doshas',
    terms:
      'surya sun major doshas सूर्य दोष pitru pitaa पिता पितृ कारक father resemblance east poorva पूर्व पूरब gold swarna स्वर्ण सोना धातु surya grahan pitru dosha surya shani sun saturn combust ego identity government heart bones eyes ninth house',
  },
  {
    id: 'reading-charts-moon-major-doshas',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'Moon Major Doshas',
    terms:
      'chandra moon major doshas चंद्र दोष chandra grahan vish yoga kemadruma moon saturn mental restlessness mother anxiety depression isolation',
  },
  {
    id: 'reading-charts-mars-major-doshas',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'Mars Major Doshas',
    terms:
      'mangal mars major doshas मंगल दोष manglik kuja dosha angarak mars rahu mars saturn marriage delay aggression accidents anger',
  },
  {
    id: 'reading-charts-jupiter-major-doshas',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'Jupiter Major Doshas',
    terms:
      'guru jupiter major doshas बृहस्पति दोष guru chandal combust jupiter debilitated capricorn wisdom children education ethics spiritual',
  },
  {
    id: 'reading-charts-venus-major-doshas',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'Venus Major Doshas',
    terms:
      'shukra venus major doshas शुक्र दोष shukra rahu combust venus shukra shani love luxury marriage creativity intimacy saturn venus',
  },
  {
    id: 'reading-charts-saturn-major-doshas',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'Saturn Major Doshas',
    terms:
      'shani saturn major doshas शनि दोष sade sati shani sade sati vish yoga moon saturn combust saturn karma authority father legal delays',
  },
  {
    id: 'reading-charts-ketu-major-doshas',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'Ketu Major Doshas',
    terms:
      'ketu major doshas केतु दोष moksha karaka मोक्ष कारक graha chandra ketu grahan surya ketu ketu shani saturn conjunction moon sun eclipse mental detachment isolation karmic south west nairutya नैऋत्य दक्षिण पश्चिम iron loha लोहा आयरन धातु',
  },
  {
    id: 'reading-charts-rahu-major-doshas',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'Rahu Major Doshas',
    terms:
      'rahu major doshas राहु दोष grahan yoga angarak guru chandal mars jupiter sun moon eclipse aggression legal spiritual confusion north west vayavya उत्तर पश्चिम वायव्य iron loha धातु लोहा आयरन',
  },
  {
    id: 'reading-charts-kala-sarpa-yoga',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'Kala Sarpa Yoga',
    terms:
      'kala sarpa kalasarpa kaal sarp yoga serpent rahu ketu all planets one side axis half chart remedial',
  },
  {
    id: 'reading-charts-kaal-sarp-12-types',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: '12 Kaal Sarp Types',
    terms:
      'kaal sarp kaalsarpa kalsarpa twelve 12 types anant kulik vasuki shankhpal padma mahapadma takshak karkotak shankhachur ghatak vishdhar sheshnag rahu ketu house lagna saptam dwitiya ashtam',
  },
  {
    id: 'reading-charts-vish-yoga',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'Vish Yoga',
    terms:
      'vish yoga poison moon saturn conjunction mental stress chart reading',
  },
  {
    id: 'reading-charts-kemadruma-yoga',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'Kemadruma Yoga',
    terms:
      'kemadruma yoga kemadruma moon alone no planet second twelfth isolation mental support moon chart',
  },
  {
    id: 'reading-charts-shakata-yoga',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'Shakata Yoga',
    terms:
      'shakata yoga moon jupiter angular positions cart instability fluctuation lunar jupiter ',
  },
  {
    id: 'reading-charts-pitru-dosha',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'Pitru Dosha',
    terms:
      'pitru dosha पितृ दोष ancestors lineage remedies shraddha ninth house father family karma',
  },
  {
    id: 'reading-charts-uday-asta',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'Uday-Asta of Planets',
    terms:
      'uday asta udayasta rise set heliacal combustion planets observable visibility graha sunrise sunset asta uday timings',
  },
  {
    id: 'reading-charts-graha-combustion',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'Graha Combustion (Asta)',
    terms:
      'combust combustion asta dagdha astangata graha sun degree orb mercury venus jupiter mars saturn moon burn close to sun d1 lagna chart check classical limits',
  },
  {
    id: 'reading-charts-panchadha-maitri',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'Panchadha Maitri',
    terms:
      'panchadha maitri panchdha maitri compound friendship planetary relationship naisargika tatkalika temporary friendship natural friendship ati mitra sama shatru ati shatru chart reading conjunction dasha interaction',
  },
  {
    id: 'reading-charts-tatva-combined',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'राशि·ग्रह·भाव तत्व · Tatva',
    terms:
      'tatva tattva element rashi graha bhava house fire earth air water ether akasha agni prithvi vayu jal pancha mahabhuta sign elements planet elements house elements north indian chart bhava cycle chart reading',
  },
  {
    id: 'reading-charts-navtara-chakra',
    groupLabel: 'नक्षत्र (Nakshatra)',
    tabLabel: 'Navtara Chakra',
    terms:
      'navtara nav tara chakra nava tara tara bala tara balam calculator navtara calculator janma nakshatra compatibility nine tara janma sampat vipat kshema pratyari sadhaka naidhana vadh mitra param mitra muhurta marriage matching naming syllables charan pada parinaam parinam 27 nakshatras moon star nakshatra devata deity remedy agni yama rudra brihaspati vishnu varuna',
  },
  {
    id: 'reading-charts-nakshatra-charan-naming',
    groupLabel: 'नक्षत्र (Nakshatra)',
    tabLabel: 'Baby names · nakshatra charan',
    terms:
      'baby name naam janma nakshatra janma nakshatra charan moon charan birth star pada quarter 108 syllables swar akshar starting letter vedic naming drik panchang aksharabhyas naamkaran nakshatra charan sounds chu che ashavini ashvini ashvinee bharani krittika rohini mrigashira ardra punarvasu pushya ashlesha magha purva phalguni uttara hasta chitra swati vishakha anuradha jyeshtha mool purva ashadha uttar shravana dhanishtha shatabhisha bhadrapada revati renaming swara siddhanta',
  },
  {
    id: 'reading-charts-lagna-mahadasha',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'Lagna-wise Mahadasha',
    terms:
      'reading charts lagna wise mahadasha lagna-wise mahadasha ascendant mahadasha mahadasha analysis sun moon mars mercury jupiter venus saturn rahu ketu mesha vrishabha mithuna karka simha kanya tula vrishchika dhanu makara kumbha meena practical dasha guide',
  },
  {
    id: 'reading-charts-vimshottari-ashtottari-dashas',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'Vimshottari · Ashtottari dasha',
    terms:
      'vimshottari vimshottari dasha mahadasha ashtottari ashtottari dasha ashtottari 120 years 108 years nine grahas eight grahas ketu excluded mahadasha antardasha nakshatra lord birth star moon nakshatra timeline parashara classical dasha system compare two dasha',
  },
  {
    id: 'lagna-baseline-mesha',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'मेष लग्न',
    terms:
      'mesha lagna aries ascendant no planets chart skeleton house ownership functional benefic malefic shubh phal ashubh phal mishrit phal mixed maraka yogakaraka kendradhipati parashara',
  },
  {
    id: 'lagna-baseline-vrishabha',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'वृषभ लग्न',
    terms:
      'vrishabha lagna taurus ascendant no planets chart skeleton house ownership functional benefic malefic shubh phal ashubh phal mishrit phal mixed maraka yogakaraka kendradhipati parashara',
  },
  {
    id: 'lagna-baseline-mithuna',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'मिथुन लग्न',
    terms:
      'mithuna lagna gemini ascendant no planets chart skeleton house ownership functional benefic malefic shubh phal ashubh phal mishrit phal mixed maraka yogakaraka kendradhipati parashara',
  },
  {
    id: 'lagna-baseline-karka',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'कर्क लग्न',
    terms:
      'karka lagna cancer ascendant no planets chart skeleton house ownership functional benefic malefic shubh phal ashubh phal mishrit phal mixed maraka yogakaraka kendradhipati parashara',
  },
  {
    id: 'lagna-baseline-simha',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'सिंह लग्न',
    terms:
      'simha lagna leo ascendant no planets chart skeleton house ownership functional benefic malefic shubh phal ashubh phal mishrit phal mixed maraka yogakaraka kendradhipati parashara',
  },
  {
    id: 'lagna-baseline-kanya',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'कन्या लग्न',
    terms:
      'kanya lagna virgo ascendant no planets chart skeleton house ownership functional benefic malefic shubh phal ashubh phal mishrit phal mixed maraka yogakaraka kendradhipati parashara',
  },
  {
    id: 'lagna-baseline-tula',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'तुला लग्न',
    terms:
      'tula lagna libra ascendant no planets chart skeleton house ownership functional benefic malefic shubh phal ashubh phal mishrit phal mixed maraka yogakaraka kendradhipati parashara',
  },
  {
    id: 'lagna-baseline-vrishchika',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'वृश्चिक लग्न',
    terms:
      'vrishchika lagna scorpio ascendant no planets chart skeleton house ownership functional benefic malefic shubh phal ashubh phal mishrit phal mixed maraka yogakaraka kendradhipati parashara',
  },
  {
    id: 'lagna-baseline-dhanu',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'धनु लग्न',
    terms:
      'dhanu lagna sagittarius ascendant no planets chart skeleton house ownership functional benefic malefic shubh phal ashubh phal mishrit phal mixed maraka yogakaraka kendradhipati parashara',
  },
  {
    id: 'lagna-baseline-makara',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'मकर लग्न',
    terms:
      'makara lagna capricorn ascendant no planets chart skeleton house ownership functional benefic malefic shubh phal ashubh phal mishrit phal mixed maraka yogakaraka kendradhipati parashara',
  },
  {
    id: 'lagna-baseline-kumbha',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'कुम्भ लग्न',
    terms:
      'kumbha lagna aquarius ascendant no planets chart skeleton house ownership functional benefic malefic shubh phal ashubh phal mishrit phal mixed maraka yogakaraka kendradhipati parashara',
  },
  {
    id: 'lagna-baseline-meena',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'मीन लग्न',
    terms:
      'meena lagna pisces ascendant no planets chart skeleton house ownership functional benefic malefic shubh phal ashubh phal mishrit phal mixed maraka yogakaraka kendradhipati parashara',
  },
  {
    id: 'reading-charts-sade-sati-calculator',
    groupLabel: 'कैलकुलेटर (Calculators)',
    tabLabel: 'Sade Sati Timeline',
    terms:
      'sade sati saade sati shani saturn transit moon rashi chandra rashi calculator timeline birth year next cycle start phase peak phase end phase saturn degree current phase reading charts mesh vrishabh mithun kark Sinh kanya tula vrishchik dhanu makar kumbh meen',
  },
  {
    id: 'my-readings-rahu-desire',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: 'Rahu and desire',
    terms:
      'my readings pause observations rahu desire study notes reflection',
  },
  {
    id: 'my-readings-retrograde-reality',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: 'Retrograde reality check',
    terms:
      'my readings pause observations retrograde vakri study notes reality check',
  },
  {
    id: 'my-readings-venus-mahadasha',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: 'Venus Mahadasha review',
    terms:
      'my readings pause observations venus shukra mahadasha review study notes',
  },
  {
    id: 'my-readings-headache-patterns',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: 'Headache pattern notes',
    terms:
      'my readings pause observations headache pattern health notes study',
  },
  {
    id: 'my-readings-loans-emi-chart',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: 'Loans, EMI & chart leverage',
    terms:
      'my readings pause loans emi debt sixth house 6th 8th 11th leverage som jyotish savings repayment dasha saturn rahu mortgage study notes',
  },
  {
    id: 'my-readings-nakshatra-remedy',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: 'One remedy per nakshatra',
    terms:
      'nakshatra remedy remedies pariksha karma money peace relationship success thread ashwini bharani krittika rohini lunar mansion birth star study notes my readings',
  },
  {
    id: 'my-readings-graha-friendly-bhavas',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: 'Graha-friendly bhavas',
    terms:
      'my readings graha planet house bhava favorable challenging strong testing sun moon mars mercury jupiter venus saturn shani rahu ketu upachaya dusthana 1st 2nd 3rd 4th 5th 6th 7th 8th 9th 10th 11th 12th navagraha which houses good bad अनुकूल चुनौतीपूर्ण भाव मजबूत कठिन सूर्य चंद्र मंगल बुध गुरु शुक्र शनि राहु केतु',
  },
  {
    id: 'my-readings-21-life-havans',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: '21 life havans',
    terms:
      'my readings havan homa yajna fire ritual lakshmi kanakdhara kubera ganapati surya saraswati maha mrityunjaya dhanvantari ayushya chandi sudarshana kaal bhairav navagraha shani rahu ketu vishnu chandra durga rudra gayatri guru brihaspati wealth career health protection planetary peace family spiritual ahuti samagri',
  },
  {
    id: 'my-readings-child-mantras',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: '11 child mantras',
    terms:
      'my readings child children kids parent parenting mantra om namo narayanaya saraswati aim ram surya chandra shanti ganesh ganapati durga shiva krishna hare ram exam fear sleep anxiety anger focus confidence bad dreams hyperactivity laziness बच्चे माता पिता मंत्र',
  },
  {
    id: 'my-readings-habits-graha',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: 'Habits & graha signals',
    terms:
      'my readings habits behaviour behavior anger temper fear anxiety moon chandra mars mangal rahu illusion lie laziness saturn shani procrastination mercury budh confusion indecision venus shukra relationship jupiter guru father sun surya screen addiction hypersensitivity fight dispute satisfaction weak planet graha signal आदत ग्रह संकेत गुस्सा डर आलस्य',
  },
  {
    id: 'my-readings-lal-kitab-21',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: '21 Lal Kitab remedies',
    terms:
      'my readings lal kitab laal kitab red book remedies upaya obstacle bada ग्रह खराब आदत सुधारो feed dogs sweet roti coconut flowing water silver moon surya jal broken electronics rahu sesame saturday crows pitru freebies venus tilak kesar coins widows elderly kitchen clean cow jaggery junk mars almonds mercury alcohol bedside water red lentils workers fish flour ग्रह खराब हो तो आदत सुधारो कुत्ते को रोटी',
  },
  {
    id: 'my-readings-navagraha-sutras',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: '9 Navagraha sutras',
    terms:
      'my readings nine 9 sutra navagraha sun moon mars mercury jupiter venus saturn rahu ketu 7th opposite 8th from moon 10th from mars happiness mercury satisfaction jupiter joy venus 3rd from saturn obsession rahu detachment ketu mental worry capability ज्योतिष के 9 सूत्र कभी गलत नहीं teaching shortcuts',
  },
  {
    id: 'my-readings-moon-silent-goals',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: 'Moon · goals in silence',
    terms:
      'my readings moon chandra silent execution silence goals plans manifestations achievements speak talk disturb 6th 8th 12th house dusthana emotions mental vibration subconscious sensitive negativity energy protect announcement मौन साधना चंद्र छठा अष्टम द्वादश भाव योजना लक्ष्य बोलना ऊर्जा',
  },
  {
    id: 'my-readings-brihat-jataka-secrets',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: 'Brihat Jataka · 11 secrets',
    terms:
      'my readings brihat jataka brihatjatakam varahamihira varaha mihir 11 secrets sun soul kalatma saturn viparitam shana hora ahoratra rajayoga karma dridha adridha mars sattvam kuja zodiac body tissues dhatu jupiter venus ministers elements fire earth water air space shloka shastra plav बृहत् जातक वराहमिहिर सूर्य आत्मा शनि होरा राजयोग',
  },
  {
    id: 'my-readings-rahu-nakshatra',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: 'Rahu in each Nakshatra',
    terms:
      'my readings rahu nakshatra ashwini bharani krittika rohini mrigashira ardra punarvasu pushya ashlesha magha purva phalguni uttara phalguni hasta chitra swati vishakha anuradha jyeshtha mula purva ashadha uttara ashadha shravana dhanishta shatabhisha purva bhadrapada uttara bhadrapada revati effects remedies rahu in each nakshatra राहु नक्षत्र उपाय प्रभाव',
  },
  {
    id: 'my-readings-ketu-conjunctions',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: 'Ketu conjunct another planet',
    terms:
      'my readings ketu conjunction sun moon mars mercury jupiter venus saturn d1 d9 navamsa detached karmic imprint spiritual authority intuitive mind warrior energy mystical writing spiritual teacher relationships discipline remedies ketu conjunct another planet केतु युति सूर्य चंद्र मंगल बुध गुरु शुक्र शनि नवांश',
  },
  {
    id: 'my-readings-sun-conjunctions',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: 'Sun conjunct another planet',
    terms:
      'my readings sun conjunction surya yuti moon mars mercury jupiter venus saturn rahu ketu amavasya budhaditya combustion asta d1 d9 navamsa authority father identity leadership remedies सूर्य युति चंद्र मंगल बुध गुरु शुक्र शनि राहु केतु अमावस्या दहन',
  },
  {
    id: 'my-readings-moon-conjunctions',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: 'Moon conjunct another planet',
    terms:
      'my readings moon conjunction chandra yuti sun mars mercury jupiter venus saturn rahu ketu mind mother emotions chandra mangal gaja kesari vish yoga grahan d1 d9 navamsa remedies चंद्र युति सूर्य मंगल बुध गुरु शुक्र शनि राहु केतु मन माता',
  },
  {
    id: 'my-readings-mars-conjunctions',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: 'Mars conjunct another planet',
    terms:
      'my readings mars conjunction mangal yuti sun moon mercury jupiter venus saturn rahu ketu courage energy chandra mangal angarak ruchaka d1 d9 navamsa remedies मंगल युति सूर्य चंद्र बुध गुरु शुक्र शनि राहु केतु ऊर्जा साहस',
  },
  {
    id: 'my-readings-mercury-conjunctions',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: 'Mercury conjunct another planet',
    terms:
      'my readings mercury conjunction budh yuti sun moon mars jupiter venus saturn rahu ketu intellect speech trade budhaditya d1 d9 navamsa remedies बुध युति सूर्य चंद्र मंगल गुरु शुक्र शनि राहु केतु बुद्धि वाणी',
  },
  {
    id: 'my-readings-jupiter-conjunctions',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: 'Jupiter conjunct another planet',
    terms:
      'my readings jupiter conjunction guru yuti sun moon mars mercury venus saturn rahu ketu wisdom dharma gaja kesari guru chandal d1 d9 navamsa remedies गुरु युति सूर्य चंद्र मंगल बुध शुक्र शनि राहु केतु ज्ञान धर्म',
  },
  {
    id: 'my-readings-venus-conjunctions',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: 'Venus conjunct another planet',
    terms:
      'my readings venus conjunction shukra yuti sun moon mars mercury jupiter saturn rahu ketu love art marriage luxury d1 d9 navamsa remedies शुक्र युति सूर्य चंद्र मंगल बुध गुरु शनि राहु केतु प्रेम कला',
  },
  {
    id: 'my-readings-saturn-conjunctions',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: 'Saturn conjunct another planet',
    terms:
      'my readings saturn conjunction shani yuti sun moon mars mercury jupiter venus rahu ketu discipline karma delay vish shrapit d1 d9 navamsa remedies शनि युति सूर्य चंद्र मंगल बुध गुरु शुक्र राहु केतु कर्म अनुशासन',
  },
  {
    id: 'my-readings-rahu-conjunctions',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: 'Rahu conjunct another planet',
    terms:
      'my readings rahu conjunction yuti sun moon mars mercury jupiter venus saturn grahan angarak guru chandal shrapit foreign obsession d1 d9 navamsa remedies राहु युति सूर्य चंद्र मंगल बुध गुरु शुक्र शनि ग्रहण',
  },
  {
    id: 'my-readings-eighth-lord-sign',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: '8th lord by sign',
    terms:
      'my readings 8th house eighth house 8th lord by sign aries taurus gemini cancer leo virgo libra scorpio sagittarius capricorn aquarius pisces longevity inheritance hidden wealth sudden events transformation occult research saturn karaka अष्टम भाव अष्टमेश राशि आयु विरासत रहस्य शनि',
  },
  {
    id: 'my-readings-jupiter-lesser-rules',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: '9 lesser-taught Jupiter notes',
    terms:
      'my readings jupiter guru lal kitab lal kitaab 9 notes lesser taught benefic mercury malefic molten gold alone exalted debilitation neecha bhanga ketu eclipse mahadasha first 8 years mars debilitated sign effects libra scorpio d1 d9 गुरु लाल किताब बुध केतु ग्रहण महादशा नीच मंगल उच्च राशि फल',
  },
  {
    id: 'my-readings-kendradhipati-doshas',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'Kendradhipati & major doshas',
    terms:
      'chart reading kendradhipati dosha kendra houses benefic jupiter venus mercury manglik chandra shani vish yoga rahu ketu kaal sarp grahan pitru daridra dosha imbalance chart context केंद्राधिपति दोष मांगलिक काल सर्प ग्रहण',
  },
  {
    id: 'my-readings-paap-graha-sthana',
    groupLabel: 'चार्ट पठन (Reading Charts)',
    tabLabel: 'Paap graha & paap sthana',
    terms:
      'chart reading paap grah papa grah paap sthan papa sthan natural malefic sun mars saturn rahu ketu dusthana 6 8 12 difficult houses chandra moon tithi पाप ग्रह पाप स्थान दुष्ठान छठा आठवाँ बारहवाँ',
  },
  {
    id: 'my-readings-daily-life-mantras',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: '7 daily life mantras',
    terms:
      'my readings daily life mantras karagre vasate lakshmi karadarshana samudra vasane devi bhumi vandana gange cha yamune godavari bath shuklambaradharam ganesh puja annam brahma meal food aditya hridayam journey travel ramaskandam hanuman sleep nightmare प्रातः करदर्शन भूमि वंदना स्नान पूजा भोजन यात्रा शयन',
  },
  {
    id: 'my-readings-rahu-seven-signs',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: "Rahu's 7 life patterns",
    terms:
      'my readings rahu seven signs life patterns multitasking shortcut validation manipulation comparison future forbidden remedy om shanti durga meditation ambition personality राहु सात संकेत जीवन पैटर्न उपाय',
  },
  {
    id: 'my-readings-surya-siddhanta-mahayuga',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: 'Surya Siddhanta Mahayuga',
    terms:
      'my readings surya siddhanta mahayuga yuga kali dvapara treta satya krita 432000 4320000 solar year 365.2588 sidereal lunar month 27.32 27.32166 synodic month 29.53 mars mangal 686.99 jupiter guru 4332 11.86 12 year pushkara kumbh saturn shani 10766 29.5 saade saati mercury budh venus shukra 88 225 orbital period mahayuga revolutions ganita phalita jyotisha nakshatra ancestors astronomy manvantara kalpa cosmic time cycles puranic cosmology ayanamsha सूर्य सिद्धान्त महायुग गुरु शनि मंगल चंद्र',
  },
  {
    id: 'my-readings-kalapurusha-body-map',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: 'Kalapurusha body map',
    terms:
      'my readings kalapurusha kaal purush body map zodiac signs body parts aries head taurus face gemini arms cancer chest leo abdomen virgo libra scorpio reproductive sagittarius thighs capricorn knees aquarius calves pisces feet malefics saturn mars rahu ketu health dasha water sign fire sign कालपुरुष शरीर राशि मेष मस्तक वृश्चिक संवेदनशील',
  },
  {
    id: 'my-readings-karaka-from-reference',
    groupLabel: 'महत्वपूर्ण अवलोकन (Important Observations)',
    tabLabel: 'Lagna + Karaka dual reading',
    terms:
      'my readings karaka from reference significator lagna chandra lagna sun moon mars mercury jupiter venus saturn 9th 10th 11th father 3rd brother 5th children 6th disease 7th spouse 8th 12th longevity house lord bhavesh dual reading predictive astrology classical rule layered judgment कारक लग्न से पढ़ना भाव स्वामी पिता संतान विवाह',
  },
  {
    id: 'jyotisha-stories-chaya-putra',
    groupLabel: 'कथाएँ (Stories)',
    tabLabel: 'छायापुत्र शनि · Chayaputra Shani',
    terms:
      'story 11.1 chayaputra shani saturn shadow sanjana sangya chaya surya sun vivasvan yama curse sun saturn enmity puranic myth katha',
  },
  {
    id: 'jyotisha-stories-brihaspati-karka-uccha',
    groupLabel: 'कथाएँ (Stories)',
    tabLabel: 'बृहस्पति कर्क में उच्च क्यों हैं',
    terms:
      'story brihaspati guru jupiter exalted cancer karka karkataka moon chandra tara budha mercury mangal mars mooltrikona dharma fourth house comfort wisdom learning scholarship metaphor purana upanishad बृहस्पति गुरु कर्क कर्कटक चंद्र तारा बुध मंगल उच्च मूलत्रिकोण धर्म चतुर्थ भाव शांति बुद्धि धी',
  },
  {
    id: 'rituals-navagraha-path',
    groupLabel: 'Rituals / Remedies',
    tabLabel: 'नवग्रह पाठ · Rituals',
    terms:
      'rituals navagraha graha remedies upaya paath path stotra stotram mantra sadhana surya aditya hridaya aditya hridayam chandra soundarya lahari saundarya lahari mangal mars skanda shashti kavach budh mercury vishnu sahasranama vishnu sahasranamam narayana kavach narayan kavacham guru jupiter dakshinamurti stotra shukra venus ashta lakshmi ashtalakshmi shani rudra suktam rudram rahu durga kavach ketu hanuman chalisa ganesh stotra',
  },
  {
    id: 'rituals-daily-graha-remedies',
    groupLabel: 'Rituals / Remedies',
    tabLabel: '9 Daily Graha Remedies',
    terms:
      'daily graha remedies nine planet remedies sun sunlight moon sleep mars exercise mercury writing journaling jupiter learning venus cleanliness saturn hard work rahu social media ketu silence lifestyle alignments astrology remedies',
  },
  {
    id: 'rituals-shani-saturday',
    groupLabel: 'Rituals / Remedies',
    tabLabel: 'शनि · आठ पत्नी मंत्र · Saturday',
    terms:
      'rituals shani shanidev saturday patni mantra eight mantras saade saati dhaiyya oil lamp mustard oil deepak japa om sham shanaishcharaya namah beeja prarthana dhvajini dhamini kankali audio smaran shloka sadhana',
  },
  {
    id: 'rituals-sun-remedies-house',
    groupLabel: 'Rituals / Remedies',
    tabLabel: 'Sun · remedies by house & sign',
    terms: 'surya sun remedies house sign rashi bhava arghya sunday ravivar father ego vitality atma upaya twelve houses',
  },
  {
    id: 'rituals-moon-remedies-house',
    groupLabel: 'Rituals / Remedies',
    tabLabel: 'Moon · remedies by house & sign',
    terms: 'chandra moon remedies house sign rashi monday somvar mother mind emotions rice milk shiva upaya',
  },
  {
    id: 'rituals-mars-remedies-house',
    groupLabel: 'Rituals / Remedies',
    tabLabel: 'Mars · remedies by house & sign',
    terms:
      'mars mangal remedies house sign rashi mangalwar tuesday red hanuman anger exercise courage upaya bhava',
  },
  {
    id: 'rituals-mercury-remedies-house',
    groupLabel: 'Rituals / Remedies',
    tabLabel: 'Mercury · remedies by house & sign',
    terms: 'budh mercury remedies house sign wednesday green speech study trade intellect upaya',
  },
  {
    id: 'rituals-jupiter-remedies-house',
    groupLabel: 'Rituals / Remedies',
    tabLabel: 'Jupiter · remedies by house & sign',
    terms: 'guru jupiter remedies house sign thursday yellow dharma charity teacher wisdom upaya',
  },
  {
    id: 'rituals-venus-remedies-house',
    groupLabel: 'Rituals / Remedies',
    tabLabel: 'Venus · remedies by house & sign',
    terms: 'shukra venus remedies house sign friday marriage art beauty relationship upaya',
  },
  {
    id: 'rituals-saturn-remedies-house',
    groupLabel: 'Rituals / Remedies',
    tabLabel: 'Saturn · remedies by house & sign',
    terms: 'shani saturn remedies house sign saturday oil sesame discipline karma patience upaya',
  },
  {
    id: 'rituals-rahu-remedies-house',
    groupLabel: 'Rituals / Remedies',
    tabLabel: 'Rahu · remedies by house & sign',
    terms: 'rahu remedies house sign ambition foreign illusion ethics guru meditation upaya',
  },
  {
    id: 'rituals-ketu-remedies-house',
    groupLabel: 'Rituals / Remedies',
    tabLabel: 'Ketu · remedies by house & sign',
    terms:
      'ketu remedies house sign rashi bhava detachment moksha dogs charity meditation south node upaya',
  },
  {
    id: 'rituals-mahadasha-avoid',
    groupLabel: 'Rituals / Remedies',
    tabLabel: 'महादशा में क्या न करें · Mahadasha avoid',
    terms:
      'mahadasha avoid dont do sun moon mars mercury jupiter venus saturn rahu ketu ego anger shortcuts discipline relationships महादशा क्या न करें सूर्य चंद्र मंगल बुध गुरु शुक्र शनि राहु केतु',
  },
  {
    id: 'rituals-sun-triple-conjunction',
    groupLabel: 'Rituals / Remedies',
    tabLabel: 'सूर्य + 3 ग्रह युति · Sun triple conjunction',
    terms:
      'sun triple conjunction three planets yoga sun moon mars mercury jupiter venus saturn leadership administration तीन ग्रह युति सूर्य चंद्र मंगल बुध गुरु शुक्र शनि',
  },
  {
    id: 'rituals-nakshatra-activities',
    groupLabel: 'Rituals / Remedies',
    tabLabel: 'नक्षत्र श्रेष्ठ कार्य · Nakshatra activities',
    terms:
      'nakshatra best activities transit moon anuradha punarvasu revati rohini shatabhisha pushya hasta mrigashira uttara phalguni dhanishta timing muhurta नक्षत्र श्रेष्ठ कार्य गोचर चंद्र',
  },
  {
    id: 'phaladeepika-ch1-s1',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.1 प्रार्थना · Prayer',
    terms:
      'phaladeepika phala deepika mantreswara chapter 1 shloka prayer philosophy jyotih param supreme light time karma dasha gochar muhurat panchang vasara masa abda pitri deva nri classical text grantha ग्रंथ फलदीपिका मंत्रेश्वर अध्याय प्रार्थना दर्शन ज्योतिः परम समय कर्म',
  },
  {
    id: 'phaladeepika-ch1-s2',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.2 राशिचक्र · Zodiac',
    terms:
      'phaladeepika rashichakra zodiac rashi sign mesha aries vrishabha taurus mithuna gemini karka cancer simha leo kanya virgo tula libra vrishchik scorpio dhanu sagittarius makara capricorn kumbha aquarius meena pisces 12 signs राशिचक्र राशि मेष वृषभ मिथुन कर्क सिंह कन्या तुला वृश्चिक धनु मकर कुम्भ मीन',
  },
  {
    id: 'phaladeepika-ch1-s3',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.3 राशि स्वामी · Sign Lords',
    terms:
      'phaladeepika sign lord rashi swami ownership mars venus mercury jupiter saturn sun moon graha स्वामी राशि ग्रह अधिपति',
  },
  {
    id: 'phaladeepika-ch1-s4',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.4 उच्च · Exaltation',
    terms:
      'phaladeepika uccha exaltation planet sign degree sun aries 10 moon taurus 3 mars capricorn 28 mercury virgo 15 jupiter cancer 5 venus pisces 27 saturn libra 20 उच्च राशि अंश ग्रह',
  },
  {
    id: 'phaladeepika-ch1-s5',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.5 नीच · Debilitation',
    terms:
      'phaladeepika neecha debilitation fall planet sign degree opposite exaltation नीच पतन ग्रह विपरीत',
  },
  {
    id: 'phaladeepika-ch1-s6',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.6 मूलत्रिकोण · Mooltrikona',
    terms:
      'phaladeepika mooltrikona mool trikona planet sign strength swakshetra own मूलत्रिकोण स्वक्षेत्र बल ग्रह',
  },
  {
    id: 'phaladeepika-ch1-s7',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.7 स्वक्षेत्र · Own Sign',
    terms:
      'phaladeepika swakshetra own sign house planet dignity स्वक्षेत्र स्वगृह अपना भाव ग्रह',
  },
  {
    id: 'phaladeepika-ch1-s8',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.8 नैसर्गिक मैत्री · Natural Friends',
    terms:
      'phaladeepika naisargik maitri natural friendship enemy neutral planet relationship friend foe नैसर्गिक मैत्री शत्रु मित्र सम ग्रह संबंध',
  },
  {
    id: 'phaladeepika-ch1-s9',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.9 तात्कालिक मैत्री · Temp Friends',
    terms:
      'phaladeepika tatkalik maitri temporary friendship placement based 2 3 4 10 11 12 houses friend enemy तात्कालिक मैत्री अस्थायी स्थान',
  },
  {
    id: 'phaladeepika-ch1-s10',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.10 पंचधा · Compound',
    terms:
      'phaladeepika panchadha compound relationship five fold adhi mitra mitra sama shatru adhi shatru पंचधा मैत्री पांच स्तर अधिमित्र अधिशत्रु',
  },
  {
    id: 'phaladeepika-ch1-s11',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.11 दृष्टि · Aspects',
    terms:
      'phaladeepika drishti aspect 7th mars 4 8 jupiter 5 9 saturn 3 10 special aspect full ग्रह दृष्टि विशेष सातवाँ',
  },
  {
    id: 'phaladeepika-ch1-s12',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.12 सूर्य · Sun',
    terms:
      'phaladeepika surya sun nature karaka atma soul father authority government fire pitta सूर्य आत्मा पिता अधिकार अग्नि',
  },
  {
    id: 'phaladeepika-ch1-s13',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.13 चंद्र · Moon',
    terms:
      'phaladeepika chandra moon nature karaka mana mind mother water kapha emotions चंद्र मन माता जल भाव',
  },
  {
    id: 'phaladeepika-ch1-s14',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.14 मंगल · Mars',
    terms:
      'phaladeepika mangal mars nature karaka courage brother land blood pitta fire energy warrior मंगल शौर्य भाई भूमि रक्त',
  },
  {
    id: 'phaladeepika-ch1-s15',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.15 बुध · Mercury',
    terms:
      'phaladeepika budha mercury nature karaka speech intelligence trade communication skin वाक् बुद्धि व्यापार वाणी बुध',
  },
  {
    id: 'phaladeepika-ch1-s16',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.16 गुरु · Jupiter',
    terms:
      'phaladeepika guru jupiter brihaspati nature karaka wisdom teacher children dharma knowledge गुरु बृहस्पति ज्ञान धर्म शिक्षा संतान',
  },
  {
    id: 'phaladeepika-ch1-s17',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.17 शुक्र · Venus',
    terms:
      'phaladeepika shukra venus nature karaka beauty love marriage luxury art vehicles wife शुक्र सौंदर्य प्रेम विवाह कला वाहन',
  },
  {
    id: 'phaladeepika-ch1-s18',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.18 शनि · Saturn',
    terms:
      'phaladeepika shani saturn nature karaka longevity sorrow servant discipline oil iron वात शनि दीर्घायु दुख सेवक अनुशासन',
  },
  {
    id: 'phaladeepika-ch1-s19',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.19 राहु · Rahu',
    terms:
      'phaladeepika rahu nature shadow planet north node illusion foreign obsession sudden राहु छाया ग्रह उत्तर माया विदेश',
  },
  {
    id: 'phaladeepika-ch1-s20',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.20 केतु · Ketu',
    terms:
      'phaladeepika ketu nature shadow planet south node moksha spiritual past life detachment केतु मोक्ष आध्यात्मिक वैराग्य',
  },
  {
    id: 'phaladeepika-ch1-s21',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.21 शुभ/अशुभ · Benefic/Malefic',
    terms:
      'phaladeepika shubha ashubha benefic malefic natural classification jupiter venus moon mercury waxing waning शुभ अशुभ पापी सौम्य ग्रह',
  },
  {
    id: 'phaladeepika-ch1-s22',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.22 षड्बल · Shadbala',
    terms:
      'phaladeepika shadbala six fold strength planet power calculation assessment षड्बल छह बल ग्रह शक्ति',
  },
  {
    id: 'phaladeepika-ch1-s23',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.23 दिग्बल · Digbala',
    terms:
      'phaladeepika digbala directional strength direction lagna 7th 4th 10th planet kendra दिग्बल दिशा बल ग्रह केन्द्र',
  },
  {
    id: 'phaladeepika-ch1-s24',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.24 कालबल · Kalabala',
    terms:
      'phaladeepika kalabala temporal strength day night hora month year planet time कालबल समय बल दिन रात होरा',
  },
  {
    id: 'phaladeepika-ch1-s25',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.25 स्थानबल · Sthanabala',
    terms:
      'phaladeepika sthanabala positional strength sign house placement uccha mooltrikona swakshetra dignity स्थानबल स्थान बल उच्च मूलत्रिकोण',
  },
  {
    id: 'phaladeepika-ch1-s26',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.26 दृक्बल · Drikbala',
    terms:
      'phaladeepika drikbala aspect strength benefic malefic glance planet दृक्बल दृष्टि बल शुभ अशुभ',
  },
  {
    id: 'phaladeepika-ch1-s27',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.27 चेष्टाबल · Cheshtabala',
    terms:
      'phaladeepika cheshtabala motional strength retrograde direct vakri speed motion चेष्टाबल गति बल वक्री मार्गी',
  },
  {
    id: 'phaladeepika-ch1-s28',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.28 नैसर्गिक बल · Natural Str',
    terms:
      'phaladeepika naisargik bala natural strength inherent order sun saturn sequence नैसर्गिक बल प्राकृतिक क्रम',
  },
  {
    id: 'phaladeepika-ch1-s29',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.29 ग्रह युद्ध · Planet War',
    terms:
      'phaladeepika graha yuddha planetary war conjunction close combat winner loser ग्रह युद्ध संयोग निकट जीत हार',
  },
  {
    id: 'phaladeepika-ch1-s30',
    groupLabel: 'फलदीपिका · अध्याय 1 · ग्रहस्वरूप',
    tabLabel: '1.30 उपसंहार · Conclusion',
    terms:
      'phaladeepika conclusion summary chapter 1 importance planet knowledge foundation graha gyan उपसंहार सारांश अध्याय ग्रह ज्ञान आधार',
  },
  {
    id: 'phaladeepika-ch2-s1',
    groupLabel: 'फलदीपिका · अध्याय 2 · भावाध्याय',
    tabLabel: '2.1 प्रथम भाव · 1st House',
    terms:
      'phaladeepika chapter 2 bhava 1st house lagna tanu self body appearance health fame प्रथम भाव लग्न तनु शरीर स्वास्थ्य भावाध्याय',
  },
  {
    id: 'phaladeepika-ch2-s2',
    groupLabel: 'फलदीपिका · अध्याय 2 · भावाध्याय',
    tabLabel: '2.2 द्वितीय भाव · 2nd House',
    terms:
      'phaladeepika 2nd house dhana wealth speech family food eye द्वितीय भाव धन वाणी कुटुम्ब भोजन',
  },
  {
    id: 'phaladeepika-ch2-s3',
    groupLabel: 'फलदीपिका · अध्याय 2 · भावाध्याय',
    tabLabel: '2.3 तृतीय भाव · 3rd House',
    terms:
      'phaladeepika 3rd house sahaja parakrama siblings courage travel arms तृतीय भाव सहज पराक्रम भाई बहन शौर्य',
  },
  {
    id: 'phaladeepika-ch2-s4',
    groupLabel: 'फलदीपिका · अध्याय 2 · भावाध्याय',
    tabLabel: '2.4 चतुर्थ भाव · 4th House',
    terms:
      'phaladeepika 4th house sukha bandhu mother happiness vehicle property education चतुर्थ भाव सुख बंधु माता वाहन संपत्ति',
  },
  {
    id: 'phaladeepika-ch2-s5',
    groupLabel: 'फलदीपिका · अध्याय 2 · भावाध्याय',
    tabLabel: '2.5 पंचम भाव · 5th House',
    terms:
      'phaladeepika 5th house putra suta children intelligence poorva punya mantra पंचम भाव पुत्र संतान बुद्धि पूर्व पुण्य',
  },
  {
    id: 'phaladeepika-ch2-s6',
    groupLabel: 'फलदीपिका · अध्याय 2 · भावाध्याय',
    tabLabel: '2.6 षष्ठ भाव · 6th House',
    terms:
      'phaladeepika 6th house ripu shatru enemies diseases debts obstacles षष्ठ भाव रिपु शत्रु रोग ऋण बाधा',
  },
  {
    id: 'phaladeepika-ch2-s7',
    groupLabel: 'फलदीपिका · अध्याय 2 · भावाध्याय',
    tabLabel: '2.7 सप्तम भाव · 7th House',
    terms:
      'phaladeepika 7th house kalatra yuvati marriage spouse partnership trade सप्तम भाव कलत्र युवती विवाह पति पत्नी',
  },
  {
    id: 'phaladeepika-ch2-s8',
    groupLabel: 'फलदीपिका · अध्याय 2 · भावाध्याय',
    tabLabel: '2.8 अष्टम भाव · 8th House',
    terms:
      'phaladeepika 8th house randhra mrityu death longevity sudden inheritance अष्टम भाव रंध्र मृत्यु आयु अचानक विरासत',
  },
  {
    id: 'phaladeepika-ch2-s9',
    groupLabel: 'फलदीपिका · अध्याय 2 · भावाध्याय',
    tabLabel: '2.9 नवम भाव · 9th House',
    terms:
      'phaladeepika 9th house dharma bhagya fortune guru father pilgrimage नवम भाव धर्म भाग्य गुरु पिता तीर्थ',
  },
  {
    id: 'phaladeepika-ch2-s10',
    groupLabel: 'फलदीपिका · अध्याय 2 · भावाध्याय',
    tabLabel: '2.10 दशम भाव · 10th House',
    terms:
      'phaladeepika 10th house karma rajya career authority fame government दशम भाव कर्म राज्य व्यवसाय अधिकार यश',
  },
  {
    id: 'phaladeepika-ch2-s11',
    groupLabel: 'फलदीपिका · अध्याय 2 · भावाध्याय',
    tabLabel: '2.11 एकादश भाव · 11th House',
    terms:
      'phaladeepika 11th house labha gains income elder siblings desires एकादश भाव लाभ आय बड़ा भाई इच्छा पूर्ति',
  },
  {
    id: 'phaladeepika-ch2-s12',
    groupLabel: 'फलदीपिका · अध्याय 2 · भावाध्याय',
    tabLabel: '2.12 द्वादश भाव · 12th House',
    terms:
      'phaladeepika 12th house vyaya losses moksha foreign expenses sleep द्वादश भाव व्यय हानि मोक्ष विदेश खर्च निद्रा',
  },
  {
    id: 'phaladeepika-ch3-s1',
    groupLabel: 'फलदीपिका · अध्याय 3 · लग्नस्वरूप',
    tabLabel: '3.1 मेष लग्न · Aries Ascendant',
    terms:
      'phaladeepika chapter 3 mesha aries ascendant lagna mars courage leadership fire मेष लग्न मंगल साहस नेतृत्व अग्नि',
  },
  {
    id: 'phaladeepika-ch3-s2',
    groupLabel: 'फलदीपिका · अध्याय 3 · लग्नस्वरूप',
    tabLabel: '3.2 वृषभ लग्न · Taurus Ascendant',
    terms:
      'phaladeepika vrishabha taurus ascendant lagna venus beauty luxury stable earth वृषभ लग्न शुक्र सौंदर्य स्थिरता पृथ्वी',
  },
  {
    id: 'phaladeepika-ch3-s3',
    groupLabel: 'फलदीपिका · अध्याय 3 · लग्नस्वरूप',
    tabLabel: '3.3 मिथुन लग्न · Gemini Ascendant',
    terms:
      'phaladeepika mithuna gemini ascendant lagna mercury communication intellect dual air मिथुन लग्न बुध संवाद बुद्धि द्विस्वभाव वायु',
  },
  {
    id: 'phaladeepika-ch3-s4',
    groupLabel: 'फलदीपिका · अध्याय 3 · लग्नस्वरूप',
    tabLabel: '3.4 कर्क लग्न · Cancer Ascendant',
    terms:
      'phaladeepika karka cancer ascendant lagna moon emotions nurturing water mother कर्क लग्न चंद्र भावनाएं पोषण जल माता',
  },
  {
    id: 'phaladeepika-ch3-s5',
    groupLabel: 'फलदीपिका · अध्याय 3 · लग्नस्वरूप',
    tabLabel: '3.5 सिंह लग्न · Leo Ascendant',
    terms:
      'phaladeepika simha leo ascendant lagna sun authority royal dignity fire king सिंह लग्न सूर्य अधिकार राजसी गरिमा अग्नि',
  },
  {
    id: 'phaladeepika-ch3-s6',
    groupLabel: 'फलदीपिका · अध्याय 3 · लग्नस्वरूप',
    tabLabel: '3.6 कन्या लग्न · Virgo Ascendant',
    terms:
      'phaladeepika kanya virgo ascendant lagna mercury analytical practical earth service कन्या लग्न बुध विश्लेषण व्यावहारिक सेवा',
  },
  {
    id: 'phaladeepika-ch3-s7',
    groupLabel: 'फलदीपिका · अध्याय 3 · लग्नस्वरूप',
    tabLabel: '3.7 तुला लग्न · Libra Ascendant',
    terms:
      'phaladeepika tula libra ascendant lagna venus balance harmony beauty trade air तुला लग्न शुक्र संतुलन सामंजस्य व्यापार',
  },
  {
    id: 'phaladeepika-ch3-s8',
    groupLabel: 'फलदीपिका · अध्याय 3 · लग्नस्वरूप',
    tabLabel: '3.8 वृश्चिक लग्न · Scorpio Ascendant',
    terms:
      'phaladeepika vrishchika scorpio ascendant lagna mars intensity transformation mystery water वृश्चिक लग्न मंगल तीव्रता परिवर्तन रहस्य',
  },
  {
    id: 'phaladeepika-ch3-s9',
    groupLabel: 'फलदीपिका · अध्याय 3 · लग्नस्वरूप',
    tabLabel: '3.9 धनु लग्न · Sagittarius Ascendant',
    terms:
      'phaladeepika dhanu sagittarius ascendant lagna jupiter wisdom dharma travel philosophy fire धनु लग्न गुरु ज्ञान धर्म यात्रा दर्शन',
  },
  {
    id: 'phaladeepika-ch3-s10',
    groupLabel: 'फलदीपिका · अध्याय 3 · लग्नस्वरूप',
    tabLabel: '3.10 मकर लग्न · Capricorn Ascendant',
    terms:
      'phaladeepika makara capricorn ascendant lagna saturn discipline ambition career earth structure मकर लग्न शनि अनुशासन महत्वाकांक्षा',
  },
  {
    id: 'phaladeepika-ch3-s11',
    groupLabel: 'फलदीपिका · अध्याय 3 · लग्नस्वरूप',
    tabLabel: '3.11 कुम्भ लग्न · Aquarius Ascendant',
    terms:
      'phaladeepika kumbha aquarius ascendant lagna saturn innovation humanitarian unconventional air कुम्भ लग्न शनि नवाचार मानवतावादी अपारंपरिक',
  },
  {
    id: 'phaladeepika-ch3-s12',
    groupLabel: 'फलदीपिका · अध्याय 3 · लग्नस्वरूप',
    tabLabel: '3.12 मीन लग्न · Pisces Ascendant',
    terms:
      'phaladeepika meena pisces ascendant lagna jupiter spiritual intuition imagination water moksha मीन लग्न गुरु आध्यात्मिक अंतर्ज्ञान कल्पना',
  },
  {
    id: 'phaladeepika-ch4-s1',
    groupLabel: 'फलदीपिका · अध्याय 4 · भावफल',
    tabLabel: '4.1 ग्रह लग्न में · Planets in 1st',
    terms:
      'phaladeepika chapter 4 planets 1st house lagna personality health appearance sun moon mars mercury jupiter venus saturn rahu ketu ग्रह प्रथम भाव लग्न व्यक्तित्व स्वास्थ्य',
  },
  {
    id: 'phaladeepika-ch4-s2',
    groupLabel: 'फलदीपिका · अध्याय 4 · भावफल',
    tabLabel: '4.2 ग्रह धन में · Planets in 2nd',
    terms:
      'phaladeepika planets 2nd house dhana wealth speech family food kutumb ग्रह द्वितीय भाव धन वाणी कुटुम्ब',
  },
  {
    id: 'phaladeepika-ch4-s3',
    groupLabel: 'फलदीपिका · अध्याय 4 · भावफल',
    tabLabel: '4.3 ग्रह तृतीय में · Planets in 3rd',
    terms:
      'phaladeepika planets 3rd house courage siblings communication travel ग्रह तृतीय भाव शौर्य भाई बहन संवाद यात्रा',
  },
  {
    id: 'phaladeepika-ch4-s4',
    groupLabel: 'फलदीपिका · अध्याय 4 · भावफल',
    tabLabel: '4.4 ग्रह सुख में · Planets in 4th',
    terms:
      'phaladeepika planets 4th house mother property vehicle happiness education ग्रह चतुर्थ भाव माता संपत्ति वाहन सुख शिक्षा',
  },
  {
    id: 'phaladeepika-ch4-s5',
    groupLabel: 'फलदीपिका · अध्याय 4 · भावफल',
    tabLabel: '4.5 ग्रह पुत्र में · Planets in 5th',
    terms:
      'phaladeepika planets 5th house children intelligence creativity poorva punya ग्रह पंचम भाव संतान बुद्धि रचनात्मकता पूर्व पुण्य',
  },
  {
    id: 'phaladeepika-ch4-s6',
    groupLabel: 'फलदीपिका · अध्याय 4 · भावफल',
    tabLabel: '4.6 ग्रह रिपु में · Planets in 6th',
    terms:
      'phaladeepika planets 6th house enemies disease debts service competition ग्रह षष्ठ भाव शत्रु रोग ऋण सेवा प्रतियोगिता',
  },
  {
    id: 'phaladeepika-ch4-s7',
    groupLabel: 'फलदीपिका · अध्याय 4 · भावफल',
    tabLabel: '4.7 ग्रह कलत्र में · Planets in 7th',
    terms:
      'phaladeepika planets 7th house marriage spouse partnership business ग्रह सप्तम भाव विवाह पति पत्नी साझेदारी व्यापार',
  },
  {
    id: 'phaladeepika-ch4-s8',
    groupLabel: 'फलदीपिका · अध्याय 4 · भावफल',
    tabLabel: '4.8 ग्रह रंध्र में · Planets in 8th',
    terms:
      'phaladeepika planets 8th house longevity sudden events inheritance transformation ग्रह अष्टम भाव आयु अचानक घटना विरासत परिवर्तन',
  },
  {
    id: 'phaladeepika-ch4-s9',
    groupLabel: 'फलदीपिका · अध्याय 4 · भावफल',
    tabLabel: '4.9 ग्रह धर्म में · Planets in 9th',
    terms:
      'phaladeepika planets 9th house fortune dharma guru father pilgrimage ग्रह नवम भाव भाग्य धर्म गुरु पिता तीर्थ',
  },
  {
    id: 'phaladeepika-ch4-s10',
    groupLabel: 'फलदीपिका · अध्याय 4 · भावफल',
    tabLabel: '4.10 ग्रह कर्म में · Planets in 10th',
    terms:
      'phaladeepika planets 10th house career fame authority government action ग्रह दशम भाव व्यवसाय यश अधिकार सरकार कर्म',
  },
  {
    id: 'phaladeepika-ch4-s11',
    groupLabel: 'फलदीपिका · अध्याय 4 · भावफल',
    tabLabel: '4.11 ग्रह लाभ में · Planets in 11th',
    terms:
      'phaladeepika planets 11th house gains income elder siblings fulfillment ग्रह एकादश भाव लाभ आय बड़ा भाई इच्छा पूर्ति',
  },
  {
    id: 'phaladeepika-ch4-s12',
    groupLabel: 'फलदीपिका · अध्याय 4 · भावफल',
    tabLabel: '4.12 ग्रह व्यय में · Planets in 12th',
    terms:
      'phaladeepika planets 12th house losses foreign moksha expenses sleep ग्रह द्वादश भाव हानि विदेश मोक्ष खर्च निद्रा',
  },
  {
    id: 'phaladeepika-ch5-s1',
    groupLabel: 'फलदीपिका · अध्याय 5 · राजयोग',
    tabLabel: '5.1 राजयोग परिचय · Introduction',
    terms:
      'phaladeepika chapter 5 raja yoga introduction royal power authority kendra trikona राजयोग परिचय शक्ति अधिकार केन्द्र त्रिकोण',
  },
  {
    id: 'phaladeepika-ch5-s2',
    groupLabel: 'फलदीपिका · अध्याय 5 · राजयोग',
    tabLabel: '5.2 केन्द्र-त्रिकोण · Kendra-Trikona',
    terms:
      'phaladeepika kendra trikona raja yoga 1 4 7 10 5 9 lords combination केन्द्र त्रिकोण राजयोग स्वामी संयोग',
  },
  {
    id: 'phaladeepika-ch5-s3',
    groupLabel: 'फलदीपिका · अध्याय 5 · राजयोग',
    tabLabel: '5.3 गजकेसरी · Gajakesari',
    terms:
      'phaladeepika gajakesari yoga jupiter moon kendra wisdom wealth fame गजकेसरी योग गुरु चंद्र ज्ञान धन यश',
  },
  {
    id: 'phaladeepika-ch5-s4',
    groupLabel: 'फलदीपिका · अध्याय 5 · राजयोग',
    tabLabel: '5.4 पंचमहापुरुष · Pancha Mahapurusha',
    terms:
      'phaladeepika pancha mahapurusha yoga ruchaka bhadra hamsa malavya shasha mars mercury jupiter venus saturn kendra पंचमहापुरुष रुचक भद्र हंस मालव्य शश',
  },
  {
    id: 'phaladeepika-ch5-s5',
    groupLabel: 'फलदीपिका · अध्याय 5 · राजयोग',
    tabLabel: '5.5 सूर्य-चंद्र · Sun-Moon',
    terms:
      'phaladeepika sun moon raja yoga luminaries power authority king सूर्य चंद्र राजयोग ज्योति शक्ति अधिकार',
  },
  {
    id: 'phaladeepika-ch5-s6',
    groupLabel: 'फलदीपिका · अध्याय 5 · राजयोग',
    tabLabel: '5.6 बुधादित्य · Budhaditya',
    terms:
      'phaladeepika budhaditya yoga mercury sun conjunction intellect status बुधादित्य योग बुध सूर्य बुद्धि पद',
  },
  {
    id: 'phaladeepika-ch5-s7',
    groupLabel: 'फलदीपिका · अध्याय 5 · राजयोग',
    tabLabel: '5.7 अमला · Amala',
    terms:
      'phaladeepika amala yoga benefic 10th house pure fame reputation अमला योग शुभ दशम यश प्रतिष्ठा',
  },
  {
    id: 'phaladeepika-ch5-s8',
    groupLabel: 'फलदीपिका · अध्याय 5 · राजयोग',
    tabLabel: '5.8 धर्म-कर्म · Dharma-Karma',
    terms:
      'phaladeepika dharma karma adhipati yoga 9th 10th lord combination fortune career धर्म कर्म अधिपति योग भाग्य व्यवसाय',
  },
  {
    id: 'phaladeepika-ch5-s9',
    groupLabel: 'फलदीपिका · अध्याय 5 · राजयोग',
    tabLabel: '5.9 विपरीत · Viparit',
    terms:
      'phaladeepika viparit raja yoga 6 8 12 lords unexpected rise reversal विपरीत राजयोग अप्रत्याशित उत्थान',
  },
  {
    id: 'phaladeepika-ch5-s10',
    groupLabel: 'फलदीपिका · अध्याय 5 · राजयोग',
    tabLabel: '5.10 नीचभंग · Neechabhanga',
    terms:
      'phaladeepika neechabhanga raja yoga debilitation cancellation power rise नीचभंग राजयोग नीच ग्रह भंग उत्थान',
  },
  {
    id: 'phaladeepika-ch5-s11',
    groupLabel: 'फलदीपिका · अध्याय 5 · राजयोग',
    tabLabel: '5.11 चामर आदि · Chamar etc',
    terms:
      'phaladeepika chamar yoga other raja yogas lesser known powerful चामर योग अन्य राजयोग',
  },
  {
    id: 'phaladeepika-ch5-s12',
    groupLabel: 'फलदीपिका · अध्याय 5 · राजयोग',
    tabLabel: '5.12 योगभंग · Yoga Bhanga',
    terms:
      'phaladeepika yoga bhanga breaking cancellation weak conditions raja yoga fail योगभंग राजयोग दुर्बल शर्त',
  },
  {
    id: 'phaladeepika-ch6-s1',
    groupLabel: 'फलदीपिका · अध्याय 6 · अरिष्ट',
    tabLabel: '6.1 अरिष्ट परिचय · Introduction',
    terms:
      'phaladeepika arishta introduction misfortune longevity ayurdaya short life अरिष्ट आयुर्दय अल्पायु दुर्भाग्य',
  },
  {
    id: 'phaladeepika-ch6-s2',
    groupLabel: 'फलदीपिका · अध्याय 6 · अरिष्ट',
    tabLabel: '6.2 बालारिष्ट · Balarishta',
    terms:
      'phaladeepika balarishta infant mortality child death moon weak lagna बालारिष्ट शिशु मृत्यु बालक चन्द्र कमजोर',
  },
  {
    id: 'phaladeepika-ch6-s3',
    groupLabel: 'फलदीपिका · अध्याय 6 · अरिष्ट',
    tabLabel: '6.3 चन्द्र अरिष्ट · Moon Arishta',
    terms:
      'phaladeepika chandra moon arishta mental health affliction hemmed malefic चन्द्र अरिष्ट मानसिक स्वास्थ्य पापकर्तरी',
  },
  {
    id: 'phaladeepika-ch6-s4',
    groupLabel: 'फलदीपिका · अध्याय 6 · अरिष्ट',
    tabLabel: '6.4 लग्न अरिष्ट · Lagna Arishta',
    terms:
      'phaladeepika lagna arishta weak ascendant malefic first house लग्न अरिष्ट कमजोर लग्नेश पाप ग्रह',
  },
  {
    id: 'phaladeepika-ch6-s5',
    groupLabel: 'फलदीपिका · अध्याय 6 · अरिष्ट',
    tabLabel: '6.5 सूर्य अरिष्ट · Sun Arishta',
    terms:
      'phaladeepika surya sun arishta father bones heart government trouble सूर्य अरिष्ट पिता हड्डी हृदय सरकार',
  },
  {
    id: 'phaladeepika-ch6-s6',
    groupLabel: 'फलदीपिका · अध्याय 6 · अरिष्ट',
    tabLabel: '6.6 मंगल अरिष्ट · Mars Arishta',
    terms:
      'phaladeepika mangal mars arishta accident surgery blood fire violence मंगल अरिष्ट दुर्घटना शल्य रक्त अग्नि हिंसा',
  },
  {
    id: 'phaladeepika-ch6-s7',
    groupLabel: 'फलदीपिका · अध्याय 6 · अरिष्ट',
    tabLabel: '6.7 बुध अरिष्ट · Mercury Arishta',
    terms:
      'phaladeepika budha mercury arishta skin nervous speech disorder बुध अरिष्ट त्वचा तंत्रिका वाक् विकार',
  },
  {
    id: 'phaladeepika-ch6-s8',
    groupLabel: 'फलदीपिका · अध्याय 6 · अरिष्ट',
    tabLabel: '6.8 गुरु अरिष्ट · Jupiter Arishta',
    terms:
      'phaladeepika guru jupiter arishta liver wisdom children financial ruin गुरु अरिष्ट यकृत विवेक सन्तान धन हानि',
  },
  {
    id: 'phaladeepika-ch6-s9',
    groupLabel: 'फलदीपिका · अध्याय 6 · अरिष्ट',
    tabLabel: '6.9 शुक्र अरिष्ट · Venus Arishta',
    terms:
      'phaladeepika shukra venus arishta reproductive marital kidney diabetes शुक्र अरिष्ट प्रजनन वैवाहिक गुर्दा मधुमेह',
  },
  {
    id: 'phaladeepika-ch6-s10',
    groupLabel: 'फलदीपिका · अध्याय 6 · अरिष्ट',
    tabLabel: '6.10 शनि अरिष्ट · Saturn Arishta',
    terms:
      'phaladeepika shani saturn arishta chronic disease poverty imprisonment delay शनि अरिष्ट दीर्घ रोग दरिद्रता कारावास विलम्ब',
  },
  {
    id: 'phaladeepika-ch6-s11',
    groupLabel: 'फलदीपिका · अध्याय 6 · अरिष्ट',
    tabLabel: '6.11 राहु-केतु अरिष्ट · Rahu-Ketu Arishta',
    terms:
      'phaladeepika rahu ketu arishta sudden poison mysterious karmic shadow राहु केतु अरिष्ट आकस्मिक विष रहस्यमय कार्मिक छाया',
  },
  {
    id: 'phaladeepika-ch6-s12',
    groupLabel: 'फलदीपिका · अध्याय 6 · अरिष्ट',
    tabLabel: '6.12 अरिष्ट भंग · Arishta Bhanga',
    terms:
      'phaladeepika arishta bhanga cancellation benefic jupiter override अरिष्ट भंग निरस्त शुभ गुरु बृहस्पति रक्षा',
  },
  {
    id: 'phaladeepika-ch7-s1',
    groupLabel: 'फलदीपिका · अध्याय 7 · आयुर्दय',
    tabLabel: '7.1 आयुर्दय परिचय · Introduction',
    terms:
      'phaladeepika ayurdaya longevity introduction lifespan alpayu madhyayu purnayu आयुर्दय दीर्घायु अल्पायु मध्यायु पूर्णायु',
  },
  {
    id: 'phaladeepika-ch7-s2',
    groupLabel: 'फलदीपिका · अध्याय 7 · आयुर्दय',
    tabLabel: '7.2 पिण्डायु विधि · Pindayu Method',
    terms:
      'phaladeepika pindayu method arc exaltation planetary contribution years पिण्डायु विधि चाप उच्च ग्रह योगदान वर्ष',
  },
  {
    id: 'phaladeepika-ch7-s3',
    groupLabel: 'फलदीपिका · अध्याय 7 · आयुर्दय',
    tabLabel: '7.3 अंशायु विधि · Amshayu Method',
    terms:
      'phaladeepika amshayu method navamsa division contribution calculation अंशायु विधि नवमांश विभाजन गणना',
  },
  {
    id: 'phaladeepika-ch7-s4',
    groupLabel: 'फलदीपिका · अध्याय 7 · आयुर्दय',
    tabLabel: '7.4 नैसर्गिक आयु · Natural Longevity',
    terms:
      'phaladeepika naisargika natural longevity sun 19 moon 25 mars 15 jupiter saturn venus mercury नैसर्गिक आयु सूर्य चन्द्र मंगल गुरु शनि शुक्र बुध',
  },
  {
    id: 'phaladeepika-ch7-s5',
    groupLabel: 'फलदीपिका · अध्याय 7 · आयुर्दय',
    tabLabel: '7.5 अष्टकवर्ग आयु · Ashtakavarga Longevity',
    terms:
      'phaladeepika ashtakavarga longevity bindus samudaya calculation अष्टकवर्ग आयु बिन्दु समुदाय गणना',
  },
  {
    id: 'phaladeepika-ch7-s6',
    groupLabel: 'फलदीपिका · अध्याय 7 · आयुर्दय',
    tabLabel: '7.6 लग्न आयु · Lagna Longevity',
    terms:
      'phaladeepika lagna longevity ascendant lord fixed movable dual sign लग्न आयु लग्नेश चर स्थिर द्विस्वभाव राशि',
  },
  {
    id: 'phaladeepika-ch7-s7',
    groupLabel: 'फलदीपिका · अध्याय 7 · आयुर्दय',
    tabLabel: '7.7 चन्द्र आयु · Moon Longevity',
    terms:
      'phaladeepika chandra moon longevity vitality full waning paksha चन्द्र आयु जीवनी शक्ति पूर्ण क्षीण पक्ष',
  },
  {
    id: 'phaladeepika-ch7-s8',
    groupLabel: 'फलदीपिका · अध्याय 7 · आयुर्दय',
    tabLabel: '7.8 ग्रह बल आयु · Planetary Strength',
    terms:
      'phaladeepika graha bala shadbala strength longevity lagna lord 8th ग्रह बल षड्बल शक्ति आयु लग्नेश अष्टमेश',
  },
  {
    id: 'phaladeepika-ch7-s9',
    groupLabel: 'फलदीपिका · अध्याय 7 · आयुर्दय',
    tabLabel: '7.9 दशा आधारित आयु · Dasha-based',
    terms:
      'phaladeepika dasha longevity dangerous period critical age maraka दशा आयु खतरनाक अवधि संकटपूर्ण आयु मारक',
  },
  {
    id: 'phaladeepika-ch7-s10',
    groupLabel: 'फलदीपिका · अध्याय 7 · आयुर्दय',
    tabLabel: '7.10 मारक ग्रह · Maraka Planets',
    terms:
      'phaladeepika maraka planets 2nd 7th house lord death inflicting मारक ग्रह द्वितीय सप्तम भाव स्वामी मृत्यु',
  },
  {
    id: 'phaladeepika-ch7-s11',
    groupLabel: 'फलदीपिका · अध्याय 7 · आयुर्दय',
    tabLabel: '7.11 मारक दशा · Maraka Dasha',
    terms:
      'phaladeepika maraka dasha timing death danger antardasha period मारक दशा समय मृत्यु खतरा अन्तर्दशा अवधि',
  },
  {
    id: 'phaladeepika-ch7-s12',
    groupLabel: 'फलदीपिका · अध्याय 7 · आयुर्दय',
    tabLabel: '7.12 आयु वृद्धि उपाय · Longevity Remedies',
    terms:
      'phaladeepika longevity remedies strengthen jupiter lagna lord mantra आयु वृद्धि उपाय गुरु लग्नेश मंत्र दीर्घायु',
  },
  {
    id: 'phaladeepika-ch8-s2',
    groupLabel: 'फलदीपिका · अध्याय 8 · दशा',
    tabLabel: '8.2 विंशोत्तरी दशा · Vimshottari',
    terms:
      'phaladeepika vimshottari dasha 120 years nakshatra cycle ketu venus sun moon mars विंशोत्तरी दशा नक्षत्र चक्र केतु शुक्र सूर्य चन्द्र मंगल',
  },
  {
    id: 'phaladeepika-ch8-s3',
    groupLabel: 'फलदीपिका · अध्याय 8 · दशा',
    tabLabel: '8.3 सूर्य दशा फल · Sun Dasha',
    terms:
      'phaladeepika sun dasha result government father prestige honor सूर्य दशा फल सरकार पिता प्रतिष्ठा सम्मान',
  },
  {
    id: 'phaladeepika-ch8-s4',
    groupLabel: 'फलदीपिका · अध्याय 8 · दशा',
    tabLabel: '8.4 चन्द्र दशा फल · Moon Dasha',
    terms:
      'phaladeepika moon chandra dasha result mind mother travel public चन्द्र दशा फल मन माता यात्रा जनसम्पर्क',
  },
  {
    id: 'phaladeepika-ch8-s5',
    groupLabel: 'फलदीपिका · अध्याय 8 · दशा',
    tabLabel: '8.5 मंगल दशा फल · Mars Dasha',
    terms:
      'phaladeepika mars mangal dasha result land courage siblings property मंगल दशा फल भूमि साहस भाई सम्पत्ति',
  },
  {
    id: 'phaladeepika-ch8-s6',
    groupLabel: 'फलदीपिका · अध्याय 8 · दशा',
    tabLabel: '8.6 राहु दशा फल · Rahu Dasha',
    terms:
      'phaladeepika rahu dasha result foreign sudden unexpected rise fall राहु दशा फल विदेश अचानक अप्रत्याशित उत्थान पतन',
  },
  {
    id: 'phaladeepika-ch8-s7',
    groupLabel: 'फलदीपिका · अध्याय 8 · दशा',
    tabLabel: '8.7 गुरु दशा फल · Jupiter Dasha',
    terms:
      'phaladeepika jupiter guru dasha result wisdom children dharma education गुरु दशा फल ज्ञान सन्तान धर्म शिक्षा',
  },
  {
    id: 'phaladeepika-ch8-s8',
    groupLabel: 'फलदीपिका · अध्याय 8 · दशा',
    tabLabel: '8.8 शनि दशा फल · Saturn Dasha',
    terms:
      'phaladeepika saturn shani dasha result karma discipline delay hardwork शनि दशा फल कर्म अनुशासन विलम्ब परिश्रम',
  },
  {
    id: 'phaladeepika-ch8-s9',
    groupLabel: 'फलदीपिका · अध्याय 8 · दशा',
    tabLabel: '8.9 बुध दशा फल · Mercury Dasha',
    terms:
      'phaladeepika mercury budha dasha result business intellect communication बुध दशा फल व्यापार बुद्धि संचार',
  },
  {
    id: 'phaladeepika-ch8-s10',
    groupLabel: 'फलदीपिका · अध्याय 8 · दशा',
    tabLabel: '8.10 केतु दशा फल · Ketu Dasha',
    terms:
      'phaladeepika ketu dasha result spiritual moksha detachment sudden केतु दशा फल आध्यात्मिक मोक्ष वैराग्य अचानक',
  },
  {
    id: 'phaladeepika-ch8-s11',
    groupLabel: 'फलदीपिका · अध्याय 8 · दशा',
    tabLabel: '8.11 शुक्र दशा फल · Venus Dasha',
    terms:
      'phaladeepika venus shukra dasha result luxury marriage pleasure vehicle शुक्र दशा फल विलासिता विवाह सुख वाहन',
  },
  {
    id: 'phaladeepika-ch8-s12',
    groupLabel: 'फलदीपिका · अध्याय 8 · दशा',
    tabLabel: '8.12 दशा विशेष नियम · Special Rules',
    terms:
      'phaladeepika dasha special rules yogakaraka maraka antardasha transit दशा विशेष नियम योगकारक मारक अन्तर्दशा गोचर',
  },
  {
    id: 'phaladeepika-ch9-s1',
    groupLabel: 'फलदीपिका · अध्याय 9 · अन्तर्दशा',
    tabLabel: '9.1 अन्तर्दशा परिचय · Introduction',
    terms:
      'phaladeepika antardasha bhukti introduction mahadasha sub period timing अन्तर्दशा भुक्ति महादशा सूक्ष्म समय',
  },
  {
    id: 'phaladeepika-ch9-s2',
    groupLabel: 'फलदीपिका · अध्याय 9 · अन्तर्दशा',
    tabLabel: '9.2 अन्तर्दशा काल विभाग · Time Division',
    terms:
      'phaladeepika antardasha duration nine parts vimshottari months अन्तर्दशा काल नौ भाग विंशोत्तरी महीने',
  },
  {
    id: 'phaladeepika-ch9-s3',
    groupLabel: 'फलदीपिका · अध्याय 9 · अन्तर्दशा',
    tabLabel: '9.3 महादशा अन्तर क्रम · Order in MD',
    terms:
      'phaladeepika antardasha order ketu venus sun moon mars rahu jupiter saturn mercury अन्तर्दशा क्रम नक्षत्र',
  },
  {
    id: 'phaladeepika-ch9-s4',
    groupLabel: 'फलदीपिका · अध्याय 9 · अन्तर्दशा',
    tabLabel: '9.4 मित्र अन्तर्दशा फल · Friendly Antar',
    terms:
      'phaladeepika friendly antardasha mutual friends benefic cooperation मित्र अन्तर्दशा सहयोग शुभ',
  },
  {
    id: 'phaladeepika-ch9-s5',
    groupLabel: 'फलदीपिका · अध्याय 9 · अन्तर्दशा',
    tabLabel: '9.5 शत्रु अन्तर्दशा फल · Enemy Antar',
    terms:
      'phaladeepika enemy antardasha tension delay dispute शत्रु अन्तर्दशा तनाव विलंब विवाद',
  },
  {
    id: 'phaladeepika-ch9-s6',
    groupLabel: 'फलदीपिका · अध्याय 9 · अन्तर्दशा',
    tabLabel: '9.6 शुभ-शुभ योग · Benefic + Benefic',
    terms:
      'phaladeepika benefic mahadasha antardasha marriage wealth yoga शुभ महादशा अन्तर विवाह धन योग',
  },
  {
    id: 'phaladeepika-ch9-s7',
    groupLabel: 'फलदीपिका · अध्याय 9 · अन्तर्दशा',
    tabLabel: '9.7 शुभ-अशुभ मिश्रण · Mixed Pairs',
    terms:
      'phaladeepika mixed antardasha malefic benefic relief stress शुभ अशुभ मिश्रण अन्तर्दशा',
  },
  {
    id: 'phaladeepika-ch9-s8',
    groupLabel: 'फलदीपिका · अध्याय 9 · अन्तर्दशा',
    tabLabel: '9.8 मारक अन्तर्दशा · Maraka Antar',
    terms:
      'phaladeepika maraka antardasha second seventh lord health मारक अन्तर्दशा द्वितीय सप्तम स्वास्थ्य',
  },
  {
    id: 'phaladeepika-ch9-s9',
    groupLabel: 'फलदीपिका · अध्याय 9 · अन्तर्दशा',
    tabLabel: '9.9 राहु-केतु अन्तर · Rahu-Ketu Antar',
    terms:
      'phaladeepika rahu ketu antardasha sudden detachment foreign राहु केतु अन्तर अचानक वैराग्य विदेश',
  },
  {
    id: 'phaladeepika-ch9-s10',
    groupLabel: 'फलदीपिका · अध्याय 9 · अन्तर्दशा',
    tabLabel: '9.10 प्रत्यन्तर्दशा · Pratyantardasha',
    terms:
      'phaladeepika pratyantardasha sub sub period weeks muhurta प्रत्यन्तर्दशा सप्ताह मुहूर्त',
  },
  {
    id: 'phaladeepika-ch9-s11',
    groupLabel: 'फलदीपिका · अध्याय 9 · अन्तर्दशा',
    tabLabel: '9.11 गोचर और अन्तर · Transit + Antar',
    terms:
      'phaladeepika transit antardasha gochar double effect timing गोचर अन्तर्दशा समय',
  },
  {
    id: 'phaladeepika-ch9-s12',
    groupLabel: 'फलदीपिका · अध्याय 9 · अन्तर्दशा',
    tabLabel: '9.12 सूक्ष्म निष्कर्ष · Fine Conclusion',
    terms:
      'phaladeepika antardasha summary practice balanced prediction अन्तर्दशा सार अभ्यास संतुलित',
  },
  {
    id: 'phaladeepika-ch10-s1',
    groupLabel: 'फलदीपिका · अध्याय 10 · अष्टकवर्ग फल',
    tabLabel: '10.1 अष्टकवर्ग फल परिचय · Introduction',
    terms:
      'phaladeepika ashtakavarga phala bindu transit dasha prediction अष्टकवर्ग फल बिन्दु गोचर दशा',
  },
  {
    id: 'phaladeepika-ch10-s2',
    groupLabel: 'फलदीपिका · अध्याय 10 · अष्टकवर्ग फल',
    tabLabel: '10.2 बिन्दु का अर्थ · Meaning of Bindu',
    terms:
      'phaladeepika bindu zero eight strength weak अष्टकवर्ग बिन्दु शून्य आठ बल',
  },
  {
    id: 'phaladeepika-ch10-s3',
    groupLabel: 'फलदीपिका · अध्याय 10 · अष्टकवर्ग फल',
    tabLabel: '10.3 पृथक अष्टकवर्ग · Individual AV',
    terms:
      'phaladeepika prithak ashtakavarga sun moon saturn row transit पृथक अष्टकवर्ग सूर्य चन्द्र शनि',
  },
  {
    id: 'phaladeepika-ch10-s4',
    groupLabel: 'फलदीपिका · अध्याय 10 · अष्टकवर्ग फल',
    tabLabel: '10.4 सर्वाष्टकवर्ग (SAV) · Sarvashtakavarga',
    terms:
      'phaladeepika sarvashtakavarga SAV total bindus per sign 300 सर्वाष्टकवर्ग कुल बिन्दु',
  },
  {
    id: 'phaladeepika-ch10-s5',
    groupLabel: 'फलदीपिका · अध्याय 10 · अष्टकवर्ग फल',
    tabLabel: '10.5 समुदाय बल · Samudaya Strength',
    terms:
      'phaladeepika samudaya kendra trikona sav strength समुदाय केन्द्र त्रिकोण बल',
  },
  {
    id: 'phaladeepika-ch10-s6',
    groupLabel: 'फलदीपिका · अध्याय 10 · अष्टकवर्ग फल',
    tabLabel: '10.6 गोचर और बिन्दु · Transit + Bindus',
    terms:
      'phaladeepika gochar bindu lagna moon transit comfort wealth गोचर बिन्दु लग्न चन्द्र',
  },
  {
    id: 'phaladeepika-ch10-s7',
    groupLabel: 'फलदीपिका · अध्याय 10 · अष्टकवर्ग फल',
    tabLabel: '10.7 अधिक बिन्दु राशि · High-Bindu Signs',
    terms:
      'phaladeepika high bindu five eight auspicious transit maraka अधिक बिन्दु शुभ गोचर',
  },
  {
    id: 'phaladeepika-ch10-s8',
    groupLabel: 'फलदीपिका · अध्याय 10 · अष्टकवर्ग फल',
    tabLabel: '10.8 कम बिन्दु राशि · Low-Bindu Signs',
    terms:
      'phaladeepika low bindu fatigue expense patience कम बिन्दु थकावट खर्च धैर्य',
  },
  {
    id: 'phaladeepika-ch10-s9',
    groupLabel: 'फलदीपिका · अध्याय 10 · अष्टकवर्ग फल',
    tabLabel: '10.9 भाव और बिन्दु · Houses + Bindus',
    terms:
      'phaladeepika bhaava lord bindu significator karaka भावेश कारक अष्टकवर्ग',
  },
  {
    id: 'phaladeepika-ch10-s10',
    groupLabel: 'फलदीपिका · अध्याय 10 · अष्टकवर्ग फल',
    tabLabel: '10.10 द्विग्रह गोचर · Double Transit',
    terms:
      'phaladeepika jupiter saturn double transit varshaphal rahu ketu द्विग्रह गोचर गुरु शनि',
  },
  {
    id: 'phaladeepika-ch10-s11',
    groupLabel: 'फलदीपिका · अध्याय 10 · अष्टकवर्ग फल',
    tabLabel: '10.11 अष्टकवर्ग उपाय · AV Remedies',
    terms:
      'phaladeepika remedy daan japa homa benefic weak row उपाय दान जप होम सौम्य',
  },
  {
    id: 'phaladeepika-ch10-s12',
    groupLabel: 'फलदीपिका · अध्याय 10 · अष्टकवर्ग फल',
    tabLabel: '10.12 दशा-गोचर संयोजन · Dasha + Gochar',
    terms:
      'phaladeepika dasha gochar ashtakavarga synthesis priority दशा गोचर संयोजन',
  },
  {
    id: 'phaladeepika-ch11-s1',
    groupLabel: 'फलदीपिका · अध्याय 11 · स्त्री जातक फल',
    tabLabel: '11.1 स्त्री जातक परिचय · Introduction',
    terms:
      'phaladeepika stri jataka women chart moon lagna ethics consent स्त्री जातक चंद्र लग्न नैतिकता',
  },
  {
    id: 'phaladeepika-ch11-s2',
    groupLabel: 'फलदीपिका · अध्याय 11 · स्त्री जातक फल',
    tabLabel: '11.2 लग्न और चंद्र · Lagna & Moon',
    terms:
      'phaladeepika moon strength benefic aspect kemadruma चंद्र बल शुभ दृष्टि केमद्रुम',
  },
  {
    id: 'phaladeepika-ch11-s3',
    groupLabel: 'फलदीपिका · अध्याय 11 · स्त्री जातक फल',
    tabLabel: '11.3 अष्टम भाव · Eighth House',
    terms:
      'phaladeepika eighth moon saturn mangalya stress अष्टम चंद्र शनि संबंध',
  },
  {
    id: 'phaladeepika-ch11-s4',
    groupLabel: 'फलदीपिका · अध्याय 11 · स्त्री जातक फल',
    tabLabel: '11.4 सप्तम और शुक्र · Seventh & Venus',
    terms:
      'phaladeepika seventh venus marriage partnership सप्तम शुक्र विवाह दाम्पत्य',
  },
  {
    id: 'phaladeepika-ch11-s5',
    groupLabel: 'फलदीपिका · अध्याय 11 · स्त्री जातक फल',
    tabLabel: '11.5 पंचम संतति · Progeny',
    terms:
      'phaladeepika fifth jupiter venus children fertility पंचम संतति गुरु संवेदनशील',
  },
  {
    id: 'phaladeepika-ch11-s6',
    groupLabel: 'फलदीपिका · अध्याय 11 · स्त्री जातक फल',
    tabLabel: '11.6 शुक्र और गुरु · Venus & Jupiter',
    terms:
      'phaladeepika venus jupiter grace dharma dasha शुक्र गुरु दशा श्री',
  },
  {
    id: 'phaladeepika-ch11-s7',
    groupLabel: 'फलदीपिका · अध्याय 11 · स्त्री जातक फल',
    tabLabel: '11.7 मंगल संयोग · Mars',
    terms:
      'phaladeepika mars moon lagna intensity dispute mangal चंद्र तीव्रता विवाद',
  },
  {
    id: 'phaladeepika-ch11-s8',
    groupLabel: 'फलदीपिका · अध्याय 11 · स्त्री जातक फल',
    tabLabel: '11.8 दुस्थान सामंजस्य · Dusthana',
    terms:
      'phaladeepika dusthana moon sixth eighth twelfth दुस्थान चंद्र षष्ठ अष्टम द्वादश',
  },
  {
    id: 'phaladeepika-ch11-s9',
    groupLabel: 'फलदीपिका · अध्याय 11 · स्त्री जातक फल',
    tabLabel: '11.9 विवाह का समय · Marriage timing',
    terms:
      'phaladeepika marriage timing venus seventh lord dasha navamsa विवाह दशा नवांश मुहूर्त',
  },
  {
    id: 'phaladeepika-ch11-s10',
    groupLabel: 'फलदीपिका · अध्याय 11 · स्त्री जातक फल',
    tabLabel: '11.10 चंद्र और स्वास्थ्य · Moon health',
    terms:
      'phaladeepika moon health sleep fluids doctor चंद्र स्वास्थ्य नींद चिकित्सक',
  },
  {
    id: 'phaladeepika-ch11-s11',
    groupLabel: 'फलदीपिका · अध्याय 11 · स्त्री जातक फल',
    tabLabel: '11.11 नैतिक सीमा · Ethics',
    terms:
      'phaladeepika ethics consent dignity humility shame नैतिकता स्वीकृति सम्मान विनम्रता',
  },
  {
    id: 'phaladeepika-ch11-s12',
    groupLabel: 'फलदीपिका · अध्याय 11 · स्त्री जातक फल',
    tabLabel: '11.12 अध्याय सार · Summary',
    terms:
      'phaladeepika stri jataka summary lagna moon seventh eighth fifth सार विवेक',
  },
];

const _rashiDeepInsertAt = JYOTISHA_TOPIC_SEARCH_INDEX.findIndex((e) => e.id === 'rashis-char-sthir-dwiswabhav');
if (_rashiDeepInsertAt >= 0) {
  JYOTISHA_TOPIC_SEARCH_INDEX.splice(_rashiDeepInsertAt, 0, ...RASHI_DEEP_DIVE_SEARCH_INDEX);
}

const _grahaGuideInsertAt = JYOTISHA_TOPIC_SEARCH_INDEX.findIndex((e) => e.id === 'planets-info');
if (_grahaGuideInsertAt >= 0) {
  JYOTISHA_TOPIC_SEARCH_INDEX.splice(_grahaGuideInsertAt, 0, ...GRAHA_GUIDE_SEARCH_INDEX);
}

function fold(s) {
  return String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function escapeRegExp(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Match whole tokens / delimited segments - avoids "bhava" matching inside "bhavat". */
function fieldMatchesWord(fieldF, w) {
  if (!fieldF || !w) return false;
  const tokens = fieldF.split(/[^a-z0-9\u0900-\u097f]+/).filter(Boolean);
  if (tokens.includes(w)) return true;
  const re = new RegExp(`(?:^|[^a-z0-9\u0900-\u097f])${escapeRegExp(w)}(?:[^a-z0-9\u0900-\u097f]|$)`);
  return re.test(fieldF);
}

function findMatchSnippet(terms, queryWords) {
  const termsList = terms.split(/\s+/);
  for (const qw of queryWords) {
    const fq = fold(qw);
    if (fq.length < 2) continue;
    for (let i = 0; i < termsList.length; i++) {
      if (fold(termsList[i]).includes(fq)) {
        const start = Math.max(0, i - 1);
        const end = Math.min(termsList.length, i + 3);
        return termsList.slice(start, end).join(' ');
      }
    }
  }
  return '';
}

/** @param {string} tabId */
function syllabusOrderIndex(tabId) {
  const idx = SYLLABUS_TAB_IDS.indexOf(tabId);
  return idx >= 0 ? idx : Number.MAX_SAFE_INTEGER;
}

/** @param {string} query */
export function searchJyotishaTopics(query) {
  const q = fold(query).trim();
  if (!q) return [];
  const queryWords = q.split(/\s+/).filter((w) => w.length >= 2);
  const kindHint = detectJyotishaKindFromQuery(q);

  // Phaladeepika shloka pages share terms with syllabus lessons (uccha, vakri, bhava…)
  // and confuse global search - browse via the Phaladeepika section instead.
  const scored = JYOTISHA_TOPIC_SEARCH_INDEX.filter(
    (entry) => getJyotishaTopicKind(entry.id) !== 'phaladeepika',
  ).map((entry) => {
    const labelF = fold(entry.tabLabel);
    const groupF = fold(entry.groupLabel);
    const termsF = fold(entry.terms);
    const hay = `${groupF} ${labelF} ${termsF}`;
    const topicKind = getJyotishaTopicKind(entry.id);
    let rank = 0;

    if (labelF.includes(q)) rank += 300;
    else if (hay.includes(q)) rank += 200;

    let matchedWordCount = 0;
    for (const w of queryWords) {
      let wordMatched = false;
      if (fieldMatchesWord(labelF, w)) {
        rank += 60;
        wordMatched = true;
      } else if (fieldMatchesWord(termsF, w)) {
        rank += 40;
        wordMatched = true;
      } else if (fieldMatchesWord(groupF, w)) {
        rank += 20;
        wordMatched = true;
      }
      if (wordMatched) matchedWordCount += 1;
    }

    if (queryWords.length > 1 && matchedWordCount > 0 && matchedWordCount < queryWords.length) {
      rank = Math.floor(rank * (matchedWordCount / queryWords.length));
    }
    if (queryWords.length > 1 && matchedWordCount === 0) rank = 0;

    if (labelF.startsWith(q)) rank += 100;
    else if (groupF.startsWith(q)) rank += 50;

    if (kindHint) {
      if (topicKind === kindHint) rank += 90;
      else if (kindHint === 'lesson' && topicKind === 'lesson') rank += 40;
      else if (kindHint !== 'lesson' && topicKind !== kindHint) rank -= 25;
    }

    if (rank <= 0) return null;

    let matchSnippet = '';
    if (!labelF.includes(q)) {
      matchSnippet = findMatchSnippet(entry.terms, queryWords);
    }

    return { entry: { ...entry, matchSnippet, kind: topicKind }, rank };
  }).filter(Boolean);

  scored.sort((a, b) => {
    const orderDiff = syllabusOrderIndex(a.entry.id) - syllabusOrderIndex(b.entry.id);
    if (orderDiff !== 0) return orderDiff;
    if (b.rank !== a.rank) return b.rank - a.rank;
    const labelA = fold(a.entry.tabLabel);
    const labelB = fold(b.entry.tabLabel);
    return labelA.localeCompare(labelB);
  });
  return scored.map((x) => x.entry);
}
